import { CITIES, cityById, type City } from '@/config/cities';
import { OFFLINE_RIVALS } from '@/config/racers';
import { GameState } from '@/core/GameState';
import type { Game, GameEvents } from '@/core/Game';
import {
  defaultSave,
  loadSave,
  storeSave,
  startTour,
  tourCityIndex,
  tourCityAt,
  isTourComplete,
  cityRecord,
  recordRaceResult,
  recordTeaserRace,
  teaserRacesLeft,
  GUEST_POT_CAP,
  type SaveData,
  type TourResult,
} from '@/core/SaveData';
import {
  championship,
  tourTrophies,
  playerIsChampion,
  tourLength,
  trophyForPlace,
  isVehicleUnlocked,
  type ChampionshipRow,
  type Trophy,
} from '@/core/Progression';
import {
  raceSkillAward,
  tourFinaleAward,
  loadoutFor,
  spBalance,
  type SkillAward,
} from '@/core/skillPoints';
import { AccountService, type AccountState, type AccountStatus } from '@/account/AccountService';
import {
  bankAward,
  bankOfflineFinale,
  markGarageDirty,
  queuePurchase,
} from '@/account/accountLogic';
import { shareGameLink, shareLinkWithText, shareReferralLink } from '@/account/referral';
import { SocialService } from '@/social/SocialService';
import { CrewController, type CrewMoment } from '@/social/CrewController';
import {
  CREW_RACE_FIELD,
  crewLink,
  crewShareText,
  resolveCrewRaceChoice,
  type CrewRaceChoice,
  type CrewRaceInvite,
} from '@/social/crewLogic';
import { RaceCrewScreen, type CrewTab } from '@/ui/social/RaceCrewScreen';
import { clubLobbyInfo } from '@/ui/social/clubRaceSheet';
import { closeSheet } from '@/ui/social/sheet';
import { shouldOfferDifficultyStep } from '@/core/difficultyCta';
import {
  cancelSwipeBack,
  installSwipeBack,
  setBackNavReducedMotion,
  setSwipeBackEnabled,
} from '@/ui/backNav';
import { isValidUsername } from '@/account/racerNames';
import { SupabaseLeaderboardProvider } from '@/account/SupabaseLeaderboardProvider';
import { LEADERBOARD_ENABLED, SP_PACKS_ENABLED } from '@/config/features';
import { spPackById } from '@/config/spPacks';
import { settleSpCredit, type SpCreditLatch } from '@/account/iapCredit';
import { SUPABASE_ENABLED } from '@/config/supabase';
import { ENTITLEMENT_ENABLED, TEASER_MODE, isTeaser } from '@/config/entitlement';
import { shouldFrontPurchaseGate } from '@/core/entitlement';
import {
  PurchaseScreen,
  type PurchaseInfo,
  type PaywallFeature,
  FEATURE_PITCH,
} from '@/ui/screens/PurchaseScreen';
import { CANONICAL_ORIGIN } from '@/config/site';
import { isNativeApp, nativeShims } from '@/native/shims';
import { shouldFrontAccountGate } from '@/native/gate';
import { VEHICLE_ORDER } from '@/entities/vehicles';
import { setPlayerLook, resolveLook } from '@/entities/carLook';
import { serializeLoadout, COSMETICS } from '@/config/cosmetics';
import { vehicleLabel } from '@/ui/vehicleCatalog';
import { TitleScreen, type TitleAccountInfo } from '@/ui/screens/TitleScreen';
import { AccountScreen } from '@/ui/screens/AccountScreen';
import { AdminScreen } from '@/ui/screens/AdminScreen';
import { CitySelectScreen } from '@/ui/screens/CitySelectScreen';
import { GarageScreen } from '@/ui/screens/GarageScreen';
import { ResultsScreen, type ResultsContext } from '@/ui/screens/ResultsScreen';
import { TourPodiumClip, type PodiumRacerSpec } from '@/ui/celebration/TourPodiumClip';
import { PauseScreen } from '@/ui/screens/PauseScreen';
import { GlobeScreen } from '@/ui/screens/GlobeScreen';
import { SettingsScreen } from '@/ui/screens/SettingsScreen';
import { OnlineScreen } from '@/ui/screens/OnlineScreen';
import { OnlineSetupScreen, type OnlineSetupConfig } from '@/ui/screens/OnlineSetupScreen';
import { TourIntermissionScreen, COUNTDOWN_SECONDS } from '@/ui/screens/TourIntermissionScreen';
import type { TourStop } from '@/ui/screens/tourProgress';
import { LobbyScreen } from '@/ui/screens/LobbyScreen';
import { RaceLobbyScreen, type RaceLobbyShowOpts } from '@/ui/screens/RaceLobbyScreen';
import { clearBlockedList, setBlockedList, type ReportContextId } from '@/social/moderation';
import {
  openBlockedList,
  openPlayerSheet,
  openReportPicker,
  type ModerationDeps,
} from '@/ui/social/playerSheet';
import { openSheet } from '@/ui/social/sheet';
import { el } from '@/ui/dom';
import { ridButton } from '@/ui/social/clubUi';
import { RaceIntroScreen } from '@/ui/screens/RaceIntroScreen';
import { LeaderboardScreen } from '@/ui/screens/LeaderboardScreen';
import { HowToPlayScreen } from '@/ui/screens/HowToPlayScreen';
import { OnboardingScreen } from '@/ui/onboarding/OnboardingScreen';
import { DemoRaceDirector, type DemoRaceDirectorCallbacks } from '@/ui/onboarding/DemoRaceDirector';
import { DEMO_FIRST_BOX_MIN_ARC } from '@/ui/onboarding/demoRaceContent';
import { ShopScreen } from '@/ui/screens/ShopScreen';
import { TransitionVeil } from '@/ui/TransitionVeil';
import { SkillPointsToast } from '@/ui/SkillPointsToast';
import { WelcomeBackToast } from '@/ui/WelcomeBackToast';
import type { GlobeFlight } from '@/world/Globe';
import { audioEngine } from '@/audio/AudioEngine';
import { music } from '@/audio/Music';
import { soundtrack } from '@/audio/Soundtrack';
import { sfxChampion, sfxCollect, sfxFanfare } from '@/audio/sfx';
import type { LinkConfig } from '@/net/LoopbackLink';
import { CrewAnchorClient, MatchmakingClient } from '@/net/MatchmakingClient';
import { MM_FIELD, MM_TOUR_LEN, type MmMatchMsg, type MmMode } from '@/net/matchmaking';
import { warmCityAssets } from '@/core/preload';
import {
  defaultRoomHost,
  networkConfig,
  probeableRoomHosts,
  roomHostByKey,
  roomHostKeyFor,
  type RoomHost,
} from '@/config/network';
import {
  CREW_ANCHOR_MARGIN_MS,
  hostKeyForRoomCode,
  ROOM_CODE_HOST_SUFFIX,
  sanitizeNetName,
} from '@/net/lobby';
import { SIM_VERSION } from '@/net/protocol';
import { nextFrame } from '@/core/scheduler';
import { markBusy, whenIdle, beginForeground, endForeground } from '@/core/idleGate';
import { reportError } from '@/dev/errorlog';
import { BUILD_TAG } from '@/config/version';
import type { WarmupProgress } from '@/core/Game';

/** What `startOnline`/`roomWsUrl` need to size + theme a friend room. */
interface OnlineConnectOpts {
  racers: number;
  /** Quick race: the city to race. */
  cityId?: string;
  /** Tour: the number of cities (2–9). Overrides `cityId`. */
  tour?: number;
  /** Tour (M87): the host's ordered city PICKS (0..tour of them). The room
   *  races these first and fills the remaining stops randomly — an empty/absent
   *  list is the fully random tour. */
  tourCities?: string[];
  /** M140: a crew race room — 'pub' = open to Quick Match searchers after
   *  FIND RACERS; 'priv' = an ordinary friend-style room for the crew. */
  crew?: 'pub' | 'priv';
}

/** M123 — the ONE display price. Server-side the real price is the Stripe
 *  Price ID in the edge function's env (never an amount in code); this string
 *  is presentation only, shown on the paywall, the title strip and the teaser
 *  results card. */
const GAME_PRICE = '$9.99';

/** M123 — sessionStorage key mirroring the in-flight purchase intent across a
 *  full-page OAuth redirect (see the `restoredIntent` latch). */
const PURCHASE_INTENT_KEY = 'dribble-racer.purchase-intent.v1';

/**
 * M123 — what the purchase-intent latch does when the account state lands.
 * PURE (the M111 predicate shape) so the state machine has tests instead of
 * comments; Flow.onState consumes it.
 *
 * The latch exists so a purchase survives account creation: a player who tapped
 * BUY, was sent to make a Racer ID and came back signed in must land back on
 * the paywall with BUY armed — NOT on the onboarding welcome (the name claim
 * can wait; a paying customer completes payment first).
 *
 *  - 'resume' — signed in, the server says they do NOT own it: re-front the
 *    paywall. This is the ONE unprompted front left in the browse-first flow,
 *    and it is intent-driven, never arrival-driven.
 *  - 'clear'  — signed in and they DO own it (they were an owner all along):
 *    the intent is moot; normal flow (onboarding, title) proceeds.
 *  - 'hold'   — no intent, or the account/wallet has not resolved ('loading',
 *    'signedOut' mid-redirect, wallet still null). Keep waiting.
 */
export function purchaseIntentOnAccountState(
  intent: boolean,
  status: AccountStatus,
  owns: boolean | null,
): 'resume' | 'clear' | 'hold' {
  if (!intent) return 'hold';
  if (status === 'signedIn' && owns === true) return 'clear';
  if (status === 'signedIn' && owns === false) return 'resume';
  return 'hold';
}

/**
 * Post-login WELCOME BACK bubble — what a pending welcome does when the
 * account state lands. PURE (tested). The welcome is armed only by a real
 * sign-in (from the Racer ID screen, or an OAuth/magic-link redirect landing),
 * never by a restored session on a normal app open.
 *
 *  - 'show' — signed in and the wallet says this is a RETURNING racer.
 *  - 'drop' — a brand-new account (onboarding is their welcome) or the
 *    sign-in never completed.
 *  - 'hold' — nothing pending, or the wallet has not answered yet.
 */
export function welcomeBackOnAccountState(
  pending: boolean,
  status: AccountStatus,
  onboarded: boolean | null,
): 'show' | 'drop' | 'hold' {
  if (!pending) return 'hold';
  if (status === 'signedOut' || status === 'disabled') return 'drop';
  if (status !== 'signedIn' || onboarded === null) return 'hold';
  return onboarded ? 'show' : 'drop';
}

/**
 * M123 — where a QUICK RACE tap goes. PURE, so the cap boundary has tests.
 *
 *  - 'full'   — the gate is not armed (owner / paywall off / unresolved):
 *    the normal quick race, exactly as today.
 *  - 'teaser' — the gate IS armed and free races remain: launch the TEASER
 *    race directly (NYC, offline, one tap from title to gameplay, no account,
 *    no paywall in the way). Browse-first: the gate meets the player at the
 *    finish line's storefront card, not at the front door.
 *  - 'cap'    — the gate is armed and the free races are spent: front the
 *    paywall (context 'cap'). SOFT — the paywall's own "play the free race"
 *    escape still races, so this is a nudge, never a wall.
 */
export function quickRaceRoute(gateArmed: boolean, racesLeft: number): 'full' | 'teaser' | 'cap' {
  if (!gateArmed) return 'full';
  return racesLeft > 0 ? 'teaser' : 'cap';
}

/**
 * Flow.ts — the game's front-of-house. It owns the save file, the menu screens
 * and the mode (Tour vs Quick Race), and drives Game through its small public
 * API. Game stays a pure race machine; every decision about WHAT to race next,
 * what it was worth and what to show lives here.
 *
 * Tour model (owner-approved): the tour always advances win or lose; points +
 * trophies per city; finishing a city in the tour unlocks it for Quick Race.
 */
export class Flow {
  private save: SaveData;
  private mode: 'quick' | 'tour' = 'quick';
  /** Non-null while an online race/lobby is live — drives rematch routing and the
   *  socket teardown when leaving. `vsAi` = private solo room vs. a friend room;
   *  `matchmade` = the room came from Quick Match / Start a Tour (M44) — no
   *  invite UI, and nothing on screen may reveal the human count. */
  private online: {
    room: string;
    vsAi: boolean;
    matchmade: boolean;
    /** The friend room's config — re-stamped onto rematch invite links. */
    opts?: OnlineConnectOpts;
    /** M140: this room is a crew race (its invite record). */
    crew?: CrewRaceInvite;
  } | null = null;
  /** Live while searching for a Quick Match / matchmade tour (M44). */
  private matchmaking: MatchmakingClient | null = null;
  /** Non-null during an ONLINE tour (M34): the accumulated official per-race results
   *  the championship is computed from, and how many cities the tour runs. */
  private onlineTour: { len: number; results: TourResult[] } | null = null;
  /** True once the server's official result for the CURRENT tour race has landed —
   *  guards the "waiting" interstitial from clobbering an already-shown result if
   *  the message beats our local finish detection. Reset at each race start. */
  private tourResultShown = false;
  /** M74: the last lobby roster's filled seats — who's watchable in spectate. */
  private rosterFilled: number[] = [];
  /** M75: a QUICK online race defers its results screen to the server's
   *  {t:'raceOver'} so early finishers spectate/wait; this latches it shown once
   *  (finishers, cut-off last car — all converge on the one raceOver). Reset per
   *  race. Unlocks earned at the local finish ride along to that screen. */
  private onlineResultShown = false;
  private pendingOnlineUnlocks: string[] = [];
  /** M78: the Skill-Point breakdown that rides along with the deferred online
   *  quick-race results (awarded at MY finish, shown at {t:'raceOver'}). */
  private pendingOnlineAward: SkillAward | null = null;
  /** M78: an offline tour's championship bonus (awarded when the final race is
   *  recorded, REVEALED on the championship summary screen). M100: `guest`
   *  marks a pot-routed bonus (rendered as the guest card, not the payout). */
  private pendingTourBonus: {
    award: SkillAward;
    newlyUnlocked: string[];
    guest?: boolean;
  } | null = null;
  /** M63: the ride we LOCKED in the race lobby — re-asserted to the room after
   *  an automatic rejoin (the reconnect would otherwise land us un-ready). */
  private lobbyLocked: string | null = null;
  /** M63 online-points latch: armed by each {t:'begin'} (one per started race),
   *  spent by the finish — a mid-race rejoin re-emitting raceFinished can never
   *  double-award lifetime points (the M62 flagged fast-follow). */
  private onlineAwardArmed = false;
  /** M100: the RACER ID layer — sign-in, cloud wallet, sync engine. */
  private account: AccountService;
  private accountState: AccountState;
  private accountScreen: AccountScreen;
  /** M121: the paid gate's front door. Off by default (ENTITLEMENT_ENABLED). */
  private purchaseScreen: PurchaseScreen;
  /** M123: a purchase is IN PROGRESS across account creation — set when the
   *  paywall routes to sign-in, cleared on every abandonment path (paywall
   *  back, choosing the free race, landing back on the title) and on success.
   *  While set, onboarding stands down and a resolved signed-in non-owner
   *  re-fronts the paywall with BUY armed (purchaseIntentOnAccountState).
   *  Mirrored to sessionStorage (setPurchaseIntent) so it survives a
   *  full-page OAuth redirect — Google sign-in is the most common sign-up
   *  path and it leaves the page entirely. */
  private purchaseIntent = false;
  /**
   * M123: the ONE-SHOT restore of a purchase intent across an OAuth redirect.
   *
   * Why a separate latch instead of restoring straight into `purchaseIntent`:
   * the post-redirect BOOT calls showTitle() (~400 ms before the account layer
   * answers), and showTitle is an abandonment path that clears the live flag —
   * a direct restore would be wiped before it could ever resume. This latch is
   * immune to that clear and is consumed exactly once, at the first RESOLVED
   * account state: signed-in non-owner → the paywall re-fronts with BUY armed;
   * anything else → it simply dies. sessionStorage is the right scope — it
   * survives the redirect round-trip but dies with the tab, and a duplicated
   * tab duplicating a shopping intent is harmless (unlike the M69 seat token).
   */
  private restoredIntent = ((): boolean => {
    try {
      const on = window.sessionStorage.getItem(PURCHASE_INTENT_KEY) === '1';
      window.sessionStorage.removeItem(PURCHASE_INTENT_KEY);
      return on;
    } catch {
      return false; // storage unavailable → the old in-memory behavior
    }
  })();

  /** The ONE writer of the intent flag — keeps the sessionStorage mirror in
   *  lockstep so a redirect can never resurrect a cleared intent. */
  private setPurchaseIntent(on: boolean): void {
    this.purchaseIntent = on;
    try {
      if (on) window.sessionStorage.setItem(PURCHASE_INTENT_KEY, '1');
      else window.sessionStorage.removeItem(PURCHASE_INTENT_KEY);
    } catch {
      /* in-memory only, as before */
    }
  }
  /** M123: the CURRENT race was launched as a free TEASER race — its finish
   *  increments save.teaserRaces (the soft cap). Set/cleared at every race
   *  launch point + on returning to the title; deliberately NOT cleared by
   *  RACE AGAIN (a replayed teaser race is still a free race). */
  private teaserRace = false;
  /** M123: why the purchase screen is up (tunes its headline tone). */
  private purchaseContext: 'gate' | 'cap' | 'return' = 'gate';
  /** The locked feature the player tapped (names the paywall headline). */
  private purchaseFeature: PaywallFeature | undefined = undefined;
  /** M123: back from Stripe's success URL, waiting for the grant to become
   *  visible — the paywall shows its calm CONFIRMING face (never an error
   *  for webhook slowness; the sync retries and refresh is already wired).
   *  Cleared the moment ownership resolves true. */
  private checkoutConfirming = false;
  /** M130 — an SP-pack success landing waiting for its credit to show:
   *  the pack id + the bought-SP mirror at boot. Consumed by onState the
   *  first time purchasedPoints exceeds `before` (toast + shop repaint).
   *  M134: inside the store app it also counts the points still OWED, so two
   *  packs bought back to back are both announced (account/iapCredit.ts). */
  private spPackReturn: SpCreditLatch | null = null;
  // ---- M156: REPORT + BLOCK + SUSPENSION (App Store Guideline 1.2) ------------------------

  /** What the report / block sheets need (built on demand — cheap). */
  private modDeps(): ModerationDeps {
    return {
      social: this.social,
      signedIn: () => this.accountState.status === 'signedIn',
      reducedMotion: () => this.save.settings.reducedMotion,
    };
  }

  /** The blocked-names list follows the signed-in account: loaded on sign-in,
   *  dropped on sign-out or when someone else signs in on this device. */
  private syncBlocks(st: AccountState): void {
    if (!this.social) return; // the very first boot states predate the service
    const uid = st.status === 'signedIn' ? st.userId : null;
    if (uid === this.blocksUser) return;
    this.blocksUser = uid;
    clearBlockedList();
    if (!uid) return;
    void this.social.myBlocks().then((rows) => {
      if (rows && this.blocksUser === uid) setBlockedList(rows);
    });
  }

  /** M156: the Community Rules page (in-app browser in the shell). */
  private openCommunityRules(): void {
    const url = `${CANONICAL_ORIGIN}/rules`;
    if (nativeShims.openExternal) nativeShims.openExternal(url);
    else window.open(url, '_blank', 'noopener');
  }

  /** Everyone else in the current (or just-finished) online race. */
  private onlineRacersForReport(): string[] {
    const live = this.game.onlineOtherRacerNames();
    if (live.length > 0) this.lastOnlineRacers = live;
    return live.length > 0 ? live : this.lastOnlineRacers;
  }

  /** REPORT A RACER: straight to the racer when there is only one. */
  private reportRacers(names: string[], context: ReportContextId): void {
    const room = this.online?.room ?? (this.game.onlineRoomCode() || null);
    if (names.length === 1) {
      openPlayerSheet({ deps: this.modDeps(), name: names[0]!, context, room });
      return;
    }
    openReportPicker({ deps: this.modDeps(), names, context, room });
  }

  /** A SUSPENDED Racer ID (a moderator upheld a report) can't race online or
   *  use the Racer Club. Says why once, then answers true (callers stop). */
  private blockSuspended(): boolean {
    if (!this.accountState.banned) return false;
    const sheet = openSheet({
      kicker: 'RACER ID',
      title: 'ACCOUNT SUSPENDED',
      reducedMotion: this.save.settings.reducedMotion,
    });
    el('div', 'crew-fine', sheet.body).textContent =
      'This Racer ID was suspended for breaking our Community Rules, so online races and the ' +
      'Racer Club are off. Offline races still work. If you think this is a mistake, contact us ' +
      'through HELP & SUPPORT in Settings.';
    const actions = el('div', 'crew-sheet-actions', sheet.body);
    ridButton(actions, { variant: 'glass', label: 'OK', noChevron: true }, () => sheet.close());
    return true;
  }

  /** M101b: the owner-only QA + growth dashboard (profiles.is_admin). */
  private adminScreen: AdminScreen;
  /** M100: coalesces the account layer's save/state repaints (a sync pass
   *  writes the save several times back-to-back). */
  private accountUiQueued = false;
  /** M100: ONE idempotency key per ONLINE QUICK race, minted at {t:'begin'} —
   *  the finish + cut-off award sites share it, so even a reload+rejoin can
   *  never post the same race twice (the server dedupes on it). */
  private onlineRaceEventId = '';
  /** M100: a guest's would-be payout riding to the deferred online results. */
  private pendingGuestAward: SkillAward | null = null;
  /** The room's field size from the last welcome (feeds the tour intro chips). */
  private lastFieldSize = 6;
  /** The online ROOMS host; default = the failover chain's head (V2 M106 —
   *  Phase A keeps that the Cloudflare worker). Overridable via ?host= (debug)
   *  or the allowlisted ?h= key an invite link carries. */
  private netBase = defaultRoomHost().url;
  /** The MATCHMAKER host — always the Cloudflare worker, whatever the rooms
   *  chain says (V2: rooms migrate, matchmaking doesn't). */
  private mmBase = networkConfig.wsUrl;
  /** A dev origin's ?host= override (local testing only — never set in a
   *  shipped build): crew rooms honour it so a local test stays local. */
  private devWsUrl: string | undefined;
  /** V2 Phase B (M107): measured /healthz round-trip per regional room host
   *  (session-lifetime; fed by probeRoomHosts at hub-open). */
  private readonly hostLatency = new Map<string, number>();
  private hostProbeStarted = false;
  /** When the last probe sweep was launched (M108b: sweeps go stale). */
  private hostProbeAt = 0;
  /** The host's last Challenge-Friends setup, remembered within the session. */
  private onlineSetup: OnlineSetupConfig = {
    mode: 'race',
    cityId: CITIES[0].id,
    racers: 4,
    tourLen: 5,
    tourCities: [], // M87: [] = random stops; ordered picks otherwise
  };

  private title: TitleScreen;
  private citySelect: CitySelectScreen;
  private garage: GarageScreen;
  private results: ResultsScreen;
  private pauseScreen: PauseScreen;
  private globeScreen: GlobeScreen;
  private settingsScreen: SettingsScreen;
  private onlineScreen: OnlineScreen;
  private onlineSetupScreen: OnlineSetupScreen;
  private lobbyScreen: LobbyScreen;
  private raceLobby: RaceLobbyScreen;
  private raceIntro: RaceIntroScreen;
  private leaderboardScreen: LeaderboardScreen;
  /** M86: the four-chapter HOW TO PLAY guide (title ? icon). */
  private howToPlayScreen: HowToPlayScreen;
  /** M101: the post-signup welcome (username claim → rule → unlock → launch).
   *  Fronted once per brand-new Racer ID — never for guests. */
  private onboarding: OnboardingScreen;
  /** M101: per-session latch — the trigger fronted (or grandfathered) once. */
  private onboardingHandled = false;
  /** M101: ?debug=1 preview runs the flow with NO server writes. */
  private onboardingPreview = false;
  /** M112: the live onboarding DEMO RACE (non-null = it owns the race view;
   *  pause + raceFinished route to it instead of the normal handlers). */
  private demoRace: DemoRaceDirector | null = null;
  private shopScreen: ShopScreen;
  /** M78: the global "+N SKILL POINTS" pop (surfaces without a results card). */
  private spToast: SkillPointsToast;
  /** The post-login "WELCOME BACK, <RACER ID>!" bubble. */
  private welcomeToast: WelcomeBackToast;
  /** Last account status seen — a sign-in is a transition INTO 'signedIn'. */
  private lastAccountStatus: AccountStatus | null = null;
  /** A sign-in just completed on the Racer ID screen — land on the home
   *  screen (not the signed-in account page) once no purchase owns the
   *  landing. */
  private loginRedirectPending = false;
  /** A real sign-in is waiting for the wallet (name + onboarded) before the
   *  welcome bubble can show. */
  private welcomePending = false;
  /** This page load IS an OAuth / magic-link redirect landing (web). */
  private readonly authLanding = new URLSearchParams(window.location.search).has('code');
  /** M94: the tour-finale podium celebration cinematic. */
  private podiumClip: TourPodiumClip;
  /** M94: the FINAL tour race's stats — shown behind the summary's toggle
   *  instead of a results screen of its own. */
  private tourFinalRace: ResultsContext | null = null;
  /** M65: black blink over hard scene swaps (globe flight → race world). */
  private veil: TransitionVeil;
  private tourIntermission: TourIntermissionScreen;
  /** Where SETTINGS returns to when closed (the title or the pause overlay). */
  private settingsReturn: 'title' | 'pause' = 'title';
  /** True while the settings screen is up — pause toggles are ignored then. */
  private settingsOpen = false;
  /** The current music mood (replayed when audio unlocks on first gesture). */
  private currentMood = 'menu';
  /** The live globe flight during a tour transition (lazy-loaded module). */
  private flight: GlobeFlight | null = null;
  private globeActive = false;
  /** Loading round: id of the offline race launch in flight — a newer
   *  launch (or hideAll) supersedes an older one's async build/warm-up. */
  private raceLaunchGen = 0;
  private garagePrewarmed = false;
  private bootWarmDone = false;
  /** Loading round: the ONLINE warm-up's latest progress, kept so the intro
   *  (which shows later, at {t:'begin'}) can paint it on arrival. */
  private onlineWarm: { frac: number; label: string; ready: boolean } | null = null;
  /** M140: RACE CREW + FRIENDS — the data layer, the crew screen, and the
   *  controller that delivers crew race invites (pop-up / in-race message /
   *  iOS push) and acts on crew + friend links. */
  private social: SocialService;
  private raceCrew: RaceCrewScreen;
  private crewCtl: CrewController;
  /** Where RACE CREW's back chevron returns to (it opens from two places). */
  private crewBack: 'online' | 'dashboard' = 'online';
  /** M156: whose blocked list is loaded (the list follows the account). */
  private blocksUser: string | null = null;
  /** M156: the last online race's other racers (real names) — REPORT A
   *  RACER still works on the standings after the race view is gone. */
  private lastOnlineRacers: string[] = [];
  /** M140: the matchmaker anchor a public crew room's FIND RACERS holds. */
  private crewAnchor: CrewAnchorClient | null = null;
  /** M140b: this player pressed FIND RACERS — once the room confirms the
   *  search, this client holds the matchmaker anchor. */
  private crewSearchPressed = false;
  /** M142 review: the tour length the club room ACTUALLY runs (from its
   *  welcome; null until then). The invite says what the starter asked for,
   *  but the first caller configures the room, so the welcome is the truth. */
  private crewRoomTour: number | null = null;
  /** Crew races this device already closed (the invite stops being offered). */
  private readonly crewRacesClosed = new Set<string>();

  constructor(
    private game: Game,
    private readonly parentEl: HTMLElement,
  ) {
    const parent = this.parentEl;
    this.save = loadSave();

    // M100: the RACER ID layer. The bridge is how the sync engine reads/writes
    // the save — every write persists AND repaints whatever account-aware UI
    // is up (coalesced). Created FIRST so screen callbacks may reference it.
    this.accountState = {
      status: SUPABASE_ENABLED ? 'loading' : 'disabled',
      userId: null,
      email: null,
      provider: null,
      rank: null,
      nickname: null,
      syncing: false,
      synced: false,
      onboarded: null,
      referralCode: null,
      refClaimed: 0,
      refPoints: 0,
      isAdmin: false,
      banned: false,
      ownsGame: null,
      baseGameSince: null,
    };
    this.account = new AccountService({
      getSave: () => this.save,
      setSave: (s) => {
        // M109: adoptGarage can move the RIDE (the account's selected car), and
        // Game.applySettings is the only writer of the sim's vehicle seam — so
        // without this the save says "muscle" while the sim still races the old
        // car, and the look gate then strips its cosmetics as a mismatch. Only
        // fires on a real change; applySettings is a no-op for the same id.
        const rideChanged = this.save.settings.playerVehicle !== s.settings.playerVehicle;
        this.save = s;
        storeSave(s);
        if (rideChanged) this.game.applySettings(s.settings);
        // M109: the account layer REPLACES the whole save (reconcileWallet /
        // adoptGarage build a new object), so the race-view look seam must be
        // republished here or the next race wears whatever was equipped before
        // signing in. M91 fixed exactly this for RESET PROGRESS and the same
        // hole was left open on the account path.
        this.syncPlayerLook();
        this.refreshAccountUi();
      },
      onState: (st) => {
        this.accountState = st;
        // M140: the live crew inbox follows the session (sign-in / switch / out).
        this.social?.onAccountState(st);
        // M156: so does the blocked-names list (never leaks across accounts).
        this.syncBlocks(st);
        // M140b: Race Crew never shows a previous account's data.
        if (st.status === 'signedIn') this.raceCrew?.setAccount(st.userId);
        else if (st.status === 'signedOut') this.raceCrew?.setAccount(null);
        this.refreshAccountUi();
        // M123: a resolved OWNER ends the confirming wait (the grant landed —
        // the refresh below flips the paywall to its success face).
        if (st.ownsGame === true) this.checkoutConfirming = false;
        // M130: the SP-pack credit landed (the sync adopted a bigger
        // purchasedPoints) — say so once, and repaint the shop's BANK.
        const credit = settleSpCredit(this.spPackReturn, this.save.purchasedPoints);
        if (credit.gained > 0) {
          this.spPackReturn = credit.latch;
          this.spToast.show(credit.gained, undefined, 'sp');
          this.shopScreen.refreshSave(this.save);
        }
        // M123: the purchase-intent state machine (pure, tested). 'clear'
        // BEFORE the onboarding check so a signer-in who turns out to already
        // own the game flows straight into the normal welcome. The restored
        // latch (an intent that survived an OAuth redirect) joins the live
        // flag here and is CONSUMED at the first resolved state either way.
        const intentStep = purchaseIntentOnAccountState(
          this.purchaseIntent || this.restoredIntent,
          st.status,
          st.ownsGame,
        );
        if (st.status === 'signedIn' || st.status === 'signedOut') this.restoredIntent = false;
        if (intentStep === 'clear') this.setPurchaseIntent(false);
        // A resumed redirect intent must land in the LIVE flag before anything
        // below reads it (the onboarding deferral in particular).
        if (intentStep === 'resume') this.setPurchaseIntent(true);
        // Post-login landing (owner): a sign-in completed on the Racer ID
        // screen goes straight to the home screen instead of repainting into
        // the signed-in account page, and a returning racer gets the WELCOME
        // BACK bubble. A purchase in progress owns its own landing (the
        // paywall resume below), so the redirect waits for it to resolve.
        const freshSignIn = st.status === 'signedIn' && this.lastAccountStatus !== 'signedIn';
        this.lastAccountStatus = st.status;
        if (freshSignIn && (this.accountScreen.isVisible || this.authLanding)) {
          this.welcomePending = true;
        }
        if (freshSignIn && this.accountScreen.isVisible) this.loginRedirectPending = true;
        if (st.status !== 'signedIn' || intentStep === 'resume') this.loginRedirectPending = false;
        if (this.loginRedirectPending && !this.purchaseIntent) {
          this.loginRedirectPending = false;
          if (this.accountScreen.isVisible) this.showTitle();
        }
        const welcome = welcomeBackOnAccountState(this.welcomePending, st.status, st.onboarded);
        if (welcome !== 'hold') this.welcomePending = false;
        if (welcome === 'show' && this.title.isVisible) {
          const name = st.nickname ?? this.save.nickname ?? 'RACER';
          this.welcomeToast.show(name, this.save.settings.reducedMotion, this.title.profileRect());
        }
        // M101: a brand-new Racer ID's first wallet pull answers "onboarded?"
        // — front the welcome (or grandfather it) the moment we know.
        // (Suppressed while purchaseIntent is set — see maybeStartOnboarding.)
        this.maybeStartOnboarding();
        // M111: the app-shell boot gate — the moment the account layer
        // resolves signed-out over the title, the RACER ID screen fronts.
        this.maybeFrontAccountGate();
        // M123: resume a purchase-in-progress — the player signed in from the
        // paywall and the server says they do NOT own it, so re-front with BUY
        // armed. The ONE unprompted front left (intent-driven, never
        // arrival-driven), and only ever over a menu surface: a sync resolving
        // mid-race must never yank the wheel (the M111/M121 rule).
        if (
          intentStep === 'resume' &&
          (this.title.isVisible || this.accountScreen.isVisible) &&
          !this.purchaseScreen.isVisible
        ) {
          this.showPurchase('gate');
        }
        // A sync landing while the purchase screen is up repaints it, so a
        // webhook granting the game mid-visit flips the screen to "you're in"
        // without a reload (and the confirming face resolves the same way).
        this.purchaseScreen.refresh(this.purchaseInfo(), st);
        // M140: crew/friend links + push registration once the account settles.
        this.crewCtl?.onAccountState(st);
      },
      // The guest pot landing in the wallet — the global "+N XP · +N SP" pop.
      onClaim: (points) => this.spToast.show(points),
    });
    // M140: the social layer rides the same session (its own RPCs + inbox).
    this.social = new SocialService(this.account);

    this.title = new TitleScreen(parent, {
      onStartTour: () => this.showGarageForTour(),
      onContinueTour: () => this.continueTour(),
      // M123: for a resolved NON-OWNER the card reads "TRY IT FREE · NO
      // SIGN-UP", so honour it — ONE tap from the title into the NYC teaser
      // race, no city grid of locked cards, no garage stop. startQuickRace's
      // own routing then handles the cap (paywall, teaser still reachable).
      // Owners and the unresolved boot window take the normal city select.
      onQuickRace: () =>
        this.purchaseGateArmed() ? this.startTeaserRace() : this.showCitySelect(),
      onPlayOnline: () => this.showOnlineHub(),
      onGarage: () => this.showGarage(),
      onLeaderboard: () => this.showLeaderboard(),
      onSettings: () => this.openSettings('title'),
      // M88: the speaker button — one tap silences the whole audio graph.
      onToggleMute: () => {
        this.save.settings.muted = !this.save.settings.muted;
        storeSave(this.save);
        this.game.applySettings(this.save.settings);
        return this.save.settings.muted;
      },
      // M102: the ⏭ button — skip the menu playlist to its next song (no-op
      // when the synth fallback is what's playing; there's nothing to skip).
      onNextTrack: () => {
        soundtrack.skipMenuTrack();
      },
      onHowToPlay: () => this.showHowToPlay(),
      // M114: the SHARE card under HOW TO PLAY — fire and forget ('dismissed'
      // gets no feedback, the M101 rule).
      onShare: () => {
        void this.shareGame();
      },
      onNickname: (name) => {
        // M103: this path is GUEST-only display naming (signed-in renames go
        // through the Racer ID dashboard's username claim) — nothing is ever
        // pushed from here. The old fire-and-forget push could rename a
        // freshly-signed-in account with a stale device-local name.
        this.save.nickname = name;
        storeSave(this.save);
      },
      // Owner round (Racer ID dashboard): the signed-in title module is one
      // button into the dashboard — the rename claim moved there.
      onAccount: () => this.showAccount(),
      onLogIn: () => this.showAccount(false, true),
      // M123: the GET THE FULL GAME strip (non-owners only) opens the paywall.
      onGetGame: () => this.showPurchase('gate'),
      // M101b: the owner-only dashboard entry (ghost row, admin flag only).
      onAdmin: () => this.showAdmin(),
      // M80: the title is fully opaque (stadium backdrop + its own 3D stage) —
      // pause the attract-world render underneath while it's up.
      onVisibleChange: (visible) => this.game.setAttractRenderPaused(visible),
    });
    this.citySelect = new CitySelectScreen(parent, {
      onPick: (idx) => this.showGarageForQuick(idx),
      onBuy: () => this.showPurchase('gate', 'cities'),
      onDifficulty: (level) => {
        this.save.settings.difficulty = level;
        storeSave(this.save);
      },
      onBack: () => this.showTitle(),
    });
    this.garage = new GarageScreen(parent);
    this.results = new ResultsScreen(parent, {
      onNextCity: () => this.advanceTour(),
      onRaceAgain: () => this.onRaceAgainPressed(),
      onChangeCity: () => {
        this.results.hide();
        if (this.online) this.showOnlineHub();
        else this.showCitySelect();
      },
      onMenu: () => {
        this.results.hide();
        this.showTitle();
      },
      // M156 (App Store 1.2): REPORT A RACER under online standings.
      onReport: () =>
        this.reportRacers(this.onlineRacersForReport(), this.onlineTour ? 'tour' : 'results'),
      // M100: the guest payout card's CREATE RACER ID push.
      onCreateAccount: () => {
        this.results.hide();
        this.showAccount();
      },
    });
    this.pauseScreen = new PauseScreen(parent, {
      onResume: () => this.togglePause(),
      onRestart: () => {
        this.pauseScreen.hide();
        this.game.beginRace();
      },
      onSettings: () => this.openSettings('pause'),
      onQuit: () => {
        this.pauseScreen.hide();
        this.showTitle();
      },
    });
    this.globeScreen = new GlobeScreen(parent, () => this.skipGlobe());
    this.settingsScreen = new SettingsScreen(parent, {
      onChange: (s) => {
        this.save.settings = s;
        storeSave(this.save);
        this.game.applySettings(s);
        setBackNavReducedMotion(s.reducedMotion); // M114b: the swipe animation too
      },
      onReset: () => {
        this.save = { ...defaultSave(), settings: this.save.settings };
        storeSave(this.save);
        // M91: the fresh save owns no cosmetics — republish the (now stock)
        // look, or the next race would still wear the pre-reset skin.
        this.syncPlayerLook();
        // M100: reset wipes the DEVICE; a signed-in Racer ID re-links on the
        // next sync and its career SP flows back — and since M110 the
        // account's GARAGE and CITY RECORDS flow back too (the account IS the
        // career, documented in docs/RACER-ID.md). A signed-OUT reset stays a
        // true device wipe.
        this.account.scheduleSync(0);
      },
      onBack: () => this.closeSettings(),
      // M102b — the ONE sign-out home (owner rule). Back to the title after,
      // where the signed-out state shows as the CREATE RACER ID module (and
      // on native, the M111 boot gate re-fronts — the app requires an ID).
      onSignOut: () => {
        void this.account.signOut().then(() => {
          if (this.settingsOpen) this.closeSettings();
        });
      },
      // M111 — permanent Racer ID deletion (store law on both app stores).
      // Success lands back on the title as a guest; on native the boot gate
      // then fronts (deleting your ID means making a new one to keep playing).
      onDeleteAccount: async (onStep) => {
        const err = await this.account.deleteAccount(onStep);
        if (!err && this.settingsOpen) this.closeSettings();
        return err;
      },
      // M111 — the in-app privacy policy link (Apple 5.1.1(i)). Opens the
      // native in-app browser in the shell, a new tab on the web.
      onPrivacy: () => {
        const url = `${CANONICAL_ORIGIN}/privacy`;
        if (nativeShims.openExternal) nativeShims.openExternal(url);
        else window.open(url, '_blank', 'noopener');
      },
      // M146 — Help & Support (public/support): the FAQ + contact form. The
      // query says where it was opened from and which build, so the page can
      // hide web-purchase answers inside the app (Apple 3.1.1) and the
      // support email names the build. Nothing personal rides in the URL.
      onSupport: () => {
        const src = isNativeApp() ? 'app' : 'web';
        const url = `${CANONICAL_ORIGIN}/support?src=${src}&b=${encodeURIComponent(BUILD_TAG)}`;
        if (nativeShims.openExternal) nativeShims.openExternal(url);
        else window.open(url, '_blank', 'noopener');
      },
      // M156 — the Community Rules (the terms every Racer ID agrees to).
      onCommunityRules: () => this.openCommunityRules(),
      onBlockedPlayers: () => openBlockedList(this.modDeps()),
    });

    // M63: online modes go STRAIGHT to their flow — the ride is picked (and
    // cycled live, for everyone to see) inside the race lobby itself, with the
    // persisted last-used ride preselected. Offline, the M68 GarageScreen is
    // the one picker (title GARAGE, Quick Race confirm, tour start).
    // Owner round (2026-10-02): a non-owner can OPEN the hub (it wears the
    // lock banner) — so every action on it carries the M121 hard guard here:
    // online is the server-side cost and must never leak to a non-owner.
    const onlineGuard =
      <A extends unknown[]>(fn: (...a: A) => void) =>
      (...a: A): void => {
        if (this.purchaseGateArmed()) {
          this.showPurchase('gate', 'online');
          return;
        }
        fn(...a);
      };
    this.onlineScreen = new OnlineScreen(parent, {
      onStartTour: onlineGuard(() => this.startMatchmaking('tour')),
      onQuickMatch: onlineGuard(() => this.startMatchmaking('quick')),
      onChallengeFriend: onlineGuard(() => this.showOnlineSetup()),
      onJoinCode: onlineGuard((code: string) => this.joinByCode(code)),
      // M140: RACE CREW opens for everyone (a web non-owner sees the lock
      // banner on it — crews are for game owners, owner rule).
      onRaceCrew: SUPABASE_ENABLED ? () => this.showRaceCrew('online') : undefined,
      onBack: () => this.showTitle(),
    });
    this.onlineSetupScreen = new OnlineSetupScreen(
      parent,
      {
        onCreate: (cfg) => this.createFriendRoom(cfg),
        onBack: () => this.showOnlineHub(),
      },
      this.onlineSetup,
    );
    this.lobbyScreen = new LobbyScreen(parent, {
      onStartAi: () => this.game.startOnlineNow(),
      onCancel: () => this.showOnlineHub(),
    });
    // M63: the online RACE LOBBY — the rotating-car showroom fronting friend
    // rooms AND matchmade rooms (ride cycling, READY lock-in, start gating).
    this.raceLobby = new RaceLobbyScreen(parent, {
      onCycleVehicle: (id) => this.game.sendLobbyVehicle(id, this.lookStringFor(id)),
      onLockIn: (id) => {
        this.applyVehicle(id); // the lock-in IS the pick — persist it like the garage
        this.lobbyLocked = id;
        this.game.sendLobbyReady(id, this.lookStringFor(id));
      },
      onRequestStart: () => this.onLobbyStart(),
      onCancel: () => this.onLobbyLeave(),
      // M81: MY showroom car wears the browsed ride's equipped cosmetics.
      lookFor: (id) => this.lookStringFor(id),
      // M156 (App Store 1.2): tap a racer's name, or REPORT A RACER.
      onPlayerName: (name) =>
        openPlayerSheet({
          deps: this.modDeps(),
          name,
          context: 'lobby',
          room: this.online?.room ?? null,
        }),
      onReportRacers: (names) => this.reportRacers(names, 'lobby'),
    });
    this.raceIntro = new RaceIntroScreen(parent);
    // Loading round: the splash is opaque — pause the attract-world render
    // under it (GPU waste, and it would move the camera the warm render uses).
    this.raceIntro.onVisibleChange = (v) => this.game.setAttractRenderPaused(v);
    // M100: the account-aware board (falls back to the local ladder inside
    // the provider whenever there's no session / no connection).
    this.leaderboardScreen = new LeaderboardScreen(
      parent,
      SUPABASE_ENABLED ? new SupabaseLeaderboardProvider(this.account) : undefined,
    );
    this.accountScreen = new AccountScreen(parent, {
      onGoogle: () => this.account.signInWithProvider('google'),
      onApple: () => this.account.signInWithProvider('apple'),
      onEmail: (email) => this.account.sendMagicLink(email),
      onCode: (email, code) => this.account.verifyEmailCode(email, code),
      onBack: () => this.showTitle(),
      // M156: the agreement line's two links (Community Rules, privacy).
      onRules: () => this.openCommunityRules(),
      onPrivacy: () => {
        const url = `${CANONICAL_ORIGIN}/privacy`;
        if (nativeShims.openExternal) nativeShims.openExternal(url);
        else window.open(url, '_blank', 'noopener');
      },
      // M101: the referral loop's permanent home (the onboarding finale is
      // its first surface; this one never goes away).
      onShare: () => this.shareReferral(),
      // The dashboard's TROPHY CABINET (podium counts read from the account).
      loadTrophies: () => this.account.fetchTrophies(),
      // Owner round (Racer ID dashboard): the rename lives on the dashboard —
      // the same unique-username claim (M101) the title used to run.
      onCheckName: async (name) => {
        if (this.accountState.status !== 'signedIn') return null;
        if (!isValidUsername(name)) return false;
        const res = await this.account.checkUsernames([name]);
        return res ? (res[name] ?? null) : null;
      },
      onClaimName: async (name) => {
        const res = await this.account.claimUsername(name);
        if (res.ok) {
          this.save.nickname = name;
          storeSave(this.save);
        }
        return res;
      },
      // M140: the dashboard's way into RACE CREW (owner rule).
      onRaceCrew: SUPABASE_ENABLED ? () => this.showRaceCrew('dashboard') : undefined,
    });
    // M121/M123 — the paid gate, now BROWSE-FIRST. Built unconditionally (it
    // is inert while ENTITLEMENT_ENABLED is false) so turning the paywall on
    // is one flag, not a code change. M123: the screen is dismissible (onBack)
    // because it only ever fronts on an ACTION — a launch-verb tap, the cap,
    // a Stripe return, or a resumed purchase intent — never on arrival.
    this.purchaseScreen = new PurchaseScreen(parent, {
      onBuy: () => this.account.startCheckout(),
      onSignIn: () => {
        // M123: the paywall sent them to make a Racer ID — the purchase is IN
        // PROGRESS and must survive the trip (onboarding stands down; a
        // resolved non-owner re-fronts this screen with BUY armed).
        this.setPurchaseIntent(true);
        this.showAccount(false);
      },
      onTerms: () => window.open(`${import.meta.env.BASE_URL}terms/`, '_blank', 'noopener'),
      // The free teaser stays reachable without an account or a purchase —
      // a cold visitor must have something to try before being asked to pay.
      onTeaser: isTeaser() ? undefined : () => this.startTeaserRace(),
      // M123: browse-first means dismissible — back lands on the title (which
      // also abandons any purchase-in-progress; showTitle clears the intent).
      onBack: () => this.showTitle(),
      // M123: the success face's CTA — the purchase is complete, the intent is
      // spent, and the title checkpoint then fronts onboarding naturally for a
      // not-yet-onboarded account (zero new onboarding code).
      onPlay: () => {
        this.checkoutConfirming = false;
        this.showTitle();
      },
    });
    this.howToPlayScreen = new HowToPlayScreen(parent);
    this.onboarding = new OnboardingScreen(parent);
    this.adminScreen = new AdminScreen(parent, {
      fetchStats: () => this.account.fetchAdminStats(),
      // M156: ADMIN → PLAYER REPORTS (App Store 1.2 moderation).
      fetchReports: (status) => this.account.fetchAdminReports(status),
      moderate: (id, action) => this.account.adminModerate(id, action),
      reducedMotion: () => this.save.settings.reducedMotion,
      onRunOnboarding: () => this.debugOnboardingPreview(),
      onHowToPlay: () => this.showHowToPlay(),
      onReplayIntro: () => window.location.reload(),
      onBack: () => this.showTitle(),
    });
    this.shopScreen = new ShopScreen(parent);
    this.veil = new TransitionVeil(parent);
    this.tourIntermission = new TourIntermissionScreen(parent, {
      onReady: () => this.game.sendReady(),
      onLeave: () => this.showTitle(),
      onReport: () => this.reportRacers(this.onlineRacersForReport(), 'tour'),
    });
    // M94: the finale celebration overlay (renderer built lazily on first play).
    this.podiumClip = new TourPodiumClip(parent);
    // M140: RACE CREW + FRIENDS.
    this.raceCrew = new RaceCrewScreen(parent, {
      social: this.social,
      onBack: () => (this.crewBack === 'dashboard' ? this.showAccount() : this.showOnlineHub()),
      onStartRace: (choice) => this.startCrewRace(choice),
      onJoinRace: (race) => this.joinCrewRace(race),
      shareCrewLink: (name, code) =>
        shareLinkWithText(
          crewLink(code, this.accountState.referralCode),
          crewShareText(name, code),
        ),
      shareFriendLink: () => this.shareReferral(),
      copyText: (text) => this.copyText(text),
      onJoinedCrew: (name) => {
        this.crewCtl.maybeAskPush(name);
        this.crewCtl.nudge();
      },
      reducedMotion: () => this.save.settings.reducedMotion,
      moderation: this.modDeps(),
    });
    this.crewCtl = new CrewController(parent, {
      social: this.social,
      account: () => this.accountState,
      moment: () => this.crewMoment(),
      currentRoom: () => this.online?.room ?? null,
      lock: () => this.paywallLock('crew'),
      kidsMode: () => this.save.settings.difficulty === 'bigkid',
      joinCrewRace: (race) => this.joinCrewRace(race),
      showCrew: (joined) => this.showRaceCrew(undefined, { welcome: joined }),
      reducedMotion: () => this.save.settings.reducedMotion,
      onBadge: (n) => {
        this.onlineScreen.setCrewBadge(n);
        this.accountScreen.setCrewBadge(n);
      },
    });
    this.social.on((e) => this.raceCrew.onSocial(e));
    // Sign-out tidies the crew layer while the session is still valid.
    this.account.beforeSignOut = () => this.crewCtl.beforeSignOut();
    // Mounted LAST so the "+N SKILL POINTS" pop layers over every screen.
    this.spToast = new SkillPointsToast(parent);
    this.welcomeToast = new WelcomeBackToast(parent);

    this.game.events.on('raceFinished', (r) => this.onRaceFinished(r));
    this.game.events.on('pauseEdge', () => this.togglePause());
    // M112: no pause screen over the demo race — its director resumes the
    // auto-paused sim silently when the tab comes back.
    this.game.events.on('autoPaused', () => {
      if (!this.demoRace) this.pauseScreen.show();
    });
    this.game.events.on('audioUnlocked', () => this.applyMood());
    // A broken/missing soundtrack file re-routes the current screen's music
    // (ultimately to the synth menu groove — the game is never silent).
    soundtrack.onUnavailable = () => this.applyMood();

    // Online lobby lifecycle (M30): the server tells us who's in, when the race
    // starts, and if the connection failed — drive the lobby screen off that.
    this.game.events.on('netWelcome', ({ racerId, city, fieldSize, tourLen, raceNum }) => {
      this.lastFieldSize = fieldSize;
      this.lobbyScreen.setCanStart(racerId === 0);
      // M142: a club race keeps its club wording ('5-stop tour · Club only ·
      // 6 racers'); the generic room line would overwrite it here. Built from
      // the WELCOME (what the room really runs), never the invite alone: if
      // they ever disagree, the lobby must say what will actually be raced.
      const crewRace = this.online?.crew;
      if (crewRace) this.crewRoomTour = tourLen;
      const info = crewRace
        ? clubLobbyInfo({ ...crewRace, tourLen, city, field: fieldSize })
        : tourLen > 0
          ? this.tourInfo(tourLen, fieldSize)
          : this.roomInfo(city, fieldSize);
      if (tourLen > 0 && (raceNum <= 1 || !this.onlineTour)) {
        this.onlineTour = { len: tourLen, results: [] };
      }
      this.lobbyScreen.setInfo(info);
      // Loading round: the welcome just built the world (Game.connectWsRace)
      // — warm its GPU state NOW, during the lobby / staging wait, so the
      // first online race starts with every shader compiled and every
      // texture uploaded instead of stalling on them mid-drive (each stall
      // online also drops sim ticks → corrections). Progress is kept for the
      // splash, which only shows at {t:'begin'}. A rejoin into the same
      // world finds it already warm (worldWarm) and skips. DEFERRED a task:
      // this event fires at the TOP of Game.buildNetRace, BEFORE the
      // synchronous city build that follows it — warming here warmed the OLD
      // world (measured live: the Lagos race inherited NYC's warm flag).
      window.setTimeout(() => this.startOnlineWarmup(), 0);
      // M63: the race lobby learns its seat/grid from the welcome (a code-join
      // knows nothing until here), and a REJOIN re-asserts our locked ride so
      // the room doesn't hold the grid waiting on a ready we already gave.
      if (this.raceLobby.isVisible) {
        this.raceLobby.setLocalSlot(racerId);
        this.raceLobby.setField(fieldSize);
        this.raceLobby.setInfo(info);
        if (crewRace) this.raceLobby.setTour(tourLen > 0);
        this.raceLobby.setReconnecting(false);
        // M109 BUG FIX: this re-assert MUST carry the locked ride's equipped
        // cosmetics. It was written at M63 when sendLobbyReady took one
        // argument; M81 added `look` WITH A DEFAULT, so this call kept
        // compiling and started sending look:'' — which the room stores as
        // the seat's look, WIPING the player's paint job on the authoritative
        // server (and, via stampNetUrlVehicle, off the rejoin URL too). It
        // fires on every matchmade race (the M64 search-in-lobby lets you LOCK
        // IN before the room even exists, so the welcome always re-asserts),
        // which is why online play rendered stock cars. The `look` parameter
        // is required now — see Game.sendLobbyVehicle.
        if (this.lobbyLocked) {
          this.game.sendLobbyReady(this.lobbyLocked, this.lookStringFor(this.lobbyLocked));
        }
      }
    });
    this.game.events.on('netLobby', ({ humans, cap }) => {
      // The same "(filled/total)" channel feeds the lobby AND the tour
      // intermission's "(ready/total)" — only the visible one shows.
      this.lobbyScreen.setWaiting(humans, cap);
      this.tourIntermission.setReadyCount(humans, cap);
    });
    this.game.events.on('netRoster', ({ seats, startRequested, search, searchMs }) => {
      this.raceLobby.setRoster(seats, startRequested);
      // M140: a public crew room's search — the lobby's countdown; once the
      // seats close, the anchor is spent and the crew invite stops offering.
      const crew = this.online?.crew;
      if (crew && search) {
        this.raceLobby.setCrewSearch(search, searchMs ?? 0);
        if (search === 'done') {
          this.crewAnchor?.close();
          this.crewAnchor = null;
          this.closeCrewInvite();
        } else if (search === 'on' && this.crewSearchPressed) {
          // M140b: offer exactly the seats that are open NOW, and keep the
          // matchmaker told as crewmates (or strangers) take them.
          const open = this.raceLobby.openSeats();
          // M142 review: the matchmaker routes by the race's DATABASE length;
          // if the room runs something else (a first caller configured it
          // differently), routing would put Start a Tour searchers into a
          // single race (or the reverse). Offer no seats then: the room's own
          // fill takes them, exactly as when nobody is searching.
          const roomMatches = this.crewRoomTour === null || this.crewRoomTour === crew.tourLen;
          if (this.crewAnchor) {
            this.crewAnchor.setSeats(open);
          } else if (roomMatches) {
            const ttlMs = (searchMs ?? 0) - CREW_ANCHOR_MARGIN_MS;
            if (ttlMs >= 3_000 && open > 0) {
              this.crewAnchor = new CrewAnchorClient(this.mmBase, {
                race: crew.id,
                room: crew.room,
                h: this.game.netHostKey ?? crew.host,
                seats: open,
                field: crew.field,
                city: crew.city,
                // M142: an open club TOUR routes START A TOUR searchers in
                // (any length); a single race routes Quick Match searchers.
                tour: crew.tourLen,
                ttlMs,
              });
            }
          }
        }
      }
      // M74: remember which seats the lobby showed as FILLED — the spectate
      // candidates after MY finish. Matchmade staged seats read as filled, so
      // the AI-backfill disguise holds while spectating.
      this.rosterFilled = seats.filter((s) => s.filled).map((s) => s.slot);
    });
    this.game.events.on('netBegin', (b) => this.onNetBegin(b));
    this.game.events.on('netReconnecting', () => {
      if (this.raceLobby.isVisible) this.raceLobby.setReconnecting(true);
    });
    this.game.events.on('netRaceStart', () => this.onNetRaceStart());
    this.game.events.on('netRaceResult', (r) => this.onNetRaceResult(r));
    this.game.events.on('netTourSync', (t) => this.onNetTourSync(t));
    this.game.events.on('netRaceOver', (o) => this.onNetRaceOver(o));
    this.game.events.on('netNextCity', (n) => this.onNetNextCity(n));
    this.game.events.on('netClosed', ({ reason }) => {
      this.raceLobby.hide();
      this.raceIntro.hide();
      // M143: a lost connection is now reported from ANY online moment (it used
      // to be only the lobby or a live race), so also clear the two tour faces
      // that would hide the error and its BACK button — the globe hop has no
      // button of its own, and the ready-up screen sits above this one.
      this.endOnlineGlobeCleanup();
      this.tourIntermission.hide();
      this.lobbyScreen.showError(reason);
    });

    this.game.applySettings(this.save.settings);
    // M114b — the left-edge swipe-back gesture (touch only). Installed once;
    // it stays inert until a menu screen arms it (see attractMenu/hideAll).
    // Listen at the DOCUMENT, not `parent` — a touch anywhere on the page must
    // reach it, and events only bubble UP (a listener on #app never sees a
    // touch that landed outside it).
    installSwipeBack();
    setBackNavReducedMotion(this.save.settings.reducedMotion);
    this.syncPlayerLook();
    // M100: boot the account layer LAST (it defers its chunk off the boot
    // path; an auth redirect landing consumes its ?code= promptly).
    // Loading round 2: an auth landing (?code=) is still consumed at once; a
    // plain boot waits for a quiet moment first — its chunk's parse is a
    // 100-500 ms task on a phone, one more thing that sat under the first tap.
    if (window.location.search.includes('code=')) this.account.init();
    else void whenIdle(400, 3000).then(() => this.account.init());
  }

  // ----- RACER ID (M100) ----------------------------------------------------

  /** The RACER ID screen — create / sign in / manage the account. M111:
   *  `required` is the app-shell boot gate face (no way back until signed in). */
  private showAccount(required = false, login = false): void {
    this.leaveOnline();
    this.hideAll();
    audioEngine.setDuck(1);
    this.setMood('menu');
    this.attractMenu(GameState.Menu);
    this.accountScreen.show(this.save, this.accountState, { required, login });
  }

  /** M111 — the app-shell Racer ID boot gate (owner-locked: apps REQUIRE an
   *  account; guests stay web-only). Checked wherever the signed-out state
   *  can surface over the title: account-state changes, title fronting, and
   *  settings closing after a sign-out/deletion. Only ever interrupts the
   *  TITLE — every menu path funnels back through it, and a live race (a
   *  session expiring mid-drive) must never be yanked. */
  private maybeFrontAccountGate(): void {
    if (!shouldFrontAccountGate(isNativeApp(), this.account.enabled, this.accountState.status)) {
      return;
    }
    if (!this.title.isVisible) return;
    // M138: a device that has had a Racer ID before (sign-out keeps
    // lastAccountId) is a RETURNING racer — the gate reads LOG IN for them;
    // a fresh install still reads CREATE YOUR RACER ID. Same buttons either
    // way (Apple / Google / email serve sign-in and sign-up alike).
    this.showAccount(true, this.save.lastAccountId !== null);
  }

  // M123 — maybeFrontPurchaseGate is GONE. The old flow fronted the paywall on
  // ARRIVAL (account resolving, title fronting, settings closing); browse-first
  // gates on ACTION instead: the five launch verbs (startQuickRace,
  // beginNewTour, continueTour, showOnlineHub, showShop) keep their hard
  // guards via purchaseGateArmed(), QUICK RACE becomes the free teaser for
  // non-owners, and the only unprompted front left is the intent-driven
  // purchase resume in onState (a player mid-purchase who just signed in).

  /** The pure rule, fed from live state. Kept separate so the hard guards on
   *  the launch verbs (and the title's storefront state) can share it. */
  private purchaseGateArmed(): boolean {
    return shouldFrontPurchaseGate({
      entitlementEnabled: ENTITLEMENT_ENABLED,
      // M124: the app shell is a PAID listing — never a paywall inside it.
      native: isNativeApp(),
      teaser: TEASER_MODE,
      accountEnabled: SUPABASE_ENABLED,
      status: this.accountState.status,
      owns: this.accountState.ownsGame,
      // Offline grace: a signed-in owner who boots on a plane must not be
      // paywalled. Locally forgeable, and accepted — the same class as the
      // existing M100 client-assertion gaps, and ONLINE play stays gated
      // server-side, which is where the real cost lives.
      cachedOwns: this.save.ownsGameCached === true,
    });
  }

  /**
   * M121 — the BOOT-PATH rule, and it is deliberately stricter than
   * {@link purchaseGateArmed}.
   *
   * The armed rule never gates while the account status is `'loading'`, which
   * is correct on the title (the account chunk resolves ~400 ms after boot, and
   * flashing a paywall at a paying customer is worse than a beat of delay).
   * But the deep-link paths run DURING that window and skip the title entirely
   * — so the armed rule let `?city=tokyo` start the full game before the
   * account had answered, and nothing re-checked afterwards because the gate
   * only ever interrupts a VISIBLE title. Measured: it started the race.
   *
   * So on the boot path, "not yet proven an owner" routes to the TITLE rather
   * than into a race. The title is where the gate resolves properly once the
   * account lands, and a known owner (live answer or the offline cache) still
   * goes straight into their deep-linked race with no delay.
   */
  private purchaseGatePossible(): boolean {
    if (!ENTITLEMENT_ENABLED || isNativeApp() || isTeaser() || !SUPABASE_ENABLED) return false;
    return !(this.accountState.ownsGame === true || this.save.ownsGameCached === true);
  }

  /** M123 — the storefront payload, built in ONE place so every surface that
   *  shows/refreshes the paywall (boot routing, onState, launch-verb guards)
   *  agrees on price, ownership and the confirming wait. */
  private purchaseInfo(): PurchaseInfo {
    const owned = this.accountState.ownsGame === true;
    return {
      price: GAME_PRICE,
      owned,
      confirming: this.checkoutConfirming && !owned,
      context: this.purchaseContext,
      feature: this.purchaseFeature,
    };
  }

  /** Owner round (2026-10-02): the lock banner a paywalled page wears for a
   *  resolved non-owner (undefined = owned / unresolved / no paywall → the
   *  page opens normally). Big Kid never sees commerce (M83): the page then
   *  simply opens unlocked-looking and the launch verbs' hard guards still
   *  hold. */
  private paywallLock(
    feature: PaywallFeature,
  ): { line: string; price: string; onBuy: () => void; onLogIn?: () => void } | undefined {
    if (!this.purchaseGateArmed() || this.save.settings.difficulty === 'bigkid') return undefined;
    return {
      line: FEATURE_PITCH[feature].sub,
      price: GAME_PRICE,
      onBuy: () => this.showPurchase('gate', feature),
      // M138 (owner): a SIGNED-OUT visitor may already own the game on their
      // Racer ID — a small LOG IN link under the banner. A signed-in
      // non-owner is already logged in: banner only. (Native never reaches
      // here: the gate is never armed in the paid app, so iOS players only
      // ever see the Racer ID sign-in, never a BUY.)
      ...(this.accountState.status === 'signedOut'
        ? { onLogIn: () => this.showAccount(false, true) }
        : {}),
    };
  }

  /** M138 (owner): a signed-out WEB visitor sees the garage as a guest — no
   *  XP / SP wallet (both belong to a Racer ID; sign-out already reset the
   *  device to a fresh guest save, see toGuestSave). Big Kid keeps its wallet
   *  (kids accrue locally, M100). On iOS signed-out never reaches a garage:
   *  the M111 Racer ID gate fronts first. */
  private isGuestView(): boolean {
    return (
      SUPABASE_ENABLED &&
      this.accountState.status === 'signedOut' &&
      this.save.settings.difficulty !== 'bigkid'
    );
  }

  /** M139 (owner): the PROMO faces are for a SIGNED-OUT web visitor who does
   *  not own the game — the garage showroom cycles every ride, the shop and
   *  customize cycle featured looks, and buying the game is the only action.
   *  A signed-in non-owner keeps the M137 banner pages. Never native. */
  private promoView(): boolean {
    return this.isGuestView() && this.paywallLock('garage') !== undefined;
  }

  /** "25 KITS · 64 FLAGS · 13 WHEELS · 13 MODS" — counted from the catalog. */
  private catalogLine(): string {
    const n = (c: string): number => COSMETICS.filter((i) => i.category === c).length;
    return `${n('paint')} KITS · ${n('wrap')} FLAGS · ${n('tires')} WHEELS · ${n('mod')} MODS`;
  }

  /** Front the paywall. `context` names WHY (the screen tunes its headline):
   *  'gate' = a guarded action / the title strip / a resumed purchase,
   *  'cap' = the teaser's free races are spent, 'return' = back from Stripe. */
  private showPurchase(
    context: 'gate' | 'cap' | 'return' = 'gate',
    feature?: PaywallFeature,
  ): void {
    // M124: the app shell is a PAID listing — the paywall must never render
    // inside it (nothing to sell, and Apple 3.1.1 forbids an outside
    // storefront). Every gate rule already says no on native; this is the
    // belt-and-braces for any future caller that forgets to ask — it lands on
    // the title so a caller expecting a screen always gets one.
    if (isNativeApp()) {
      this.showTitle();
      return;
    }
    this.purchaseContext = context;
    this.purchaseFeature = feature;
    this.leaveOnline();
    this.hideAll();
    audioEngine.setDuck(1);
    this.setMood('menu');
    this.attractMenu(GameState.Menu);
    this.purchaseScreen.show(this.purchaseInfo(), this.accountState);
  }

  /** The free teaser a cold visitor can try without an account or a purchase:
   *  the starter city, single player. Deliberately the same code path a normal
   *  quick race takes — the teaser is a CONFIG, not a fork. M123: this is also
   *  where a non-owner's QUICK RACE tap lands (quickRaceRoute), and the
   *  paywall's own "play the free race" escape — which deliberately works
   *  even at/past the cap (soft, not hard). */
  private startTeaserRace(): void {
    // Choosing to play is choosing not to buy right now — the purchase flow
    // is abandoned (it re-arms the moment they tap BUY / sign-in again).
    this.setPurchaseIntent(false);
    this.purchaseScreen.hide();
    this.startQuickRace(0, true);
  }

  /**
   * M123 — the title's storefront tri-state.
   *  false = a RESOLVED non-owner (exactly when the gate is armed): the title
   *          shows the GET THE FULL GAME strip + the try-it-free Quick Race sub.
   *  true  = an owner, a paywall-off/teaser build, or the offline-grace cache:
   *          the title is byte-identical to today.
   *  null  = unresolved (account loading / wallet silent): ALSO unchanged —
   *          never advertise at a possible owner.
   */
  private titleOwnership(): boolean | null {
    // M124: inside the paid app shell everyone is an owner — the title is
    // byte-identical to an owner's (no storefront strip, no TRY IT FREE sub).
    if (!ENTITLEMENT_ENABLED || isNativeApp() || isTeaser() || !SUPABASE_ENABLED) return true;
    if (this.purchaseGateArmed()) return false;
    if (this.accountState.ownsGame === true || this.save.ownsGameCached === true) return true;
    return null;
  }

  private titleAccountInfo(): TitleAccountInfo {
    return {
      status: this.accountState.status,
      rank: this.accountState.rank,
      guestPoints: this.save.guestPoints,
      isAdmin: this.accountState.isAdmin,
      // M103: the account's server-side username — the signed-in identity.
      nickname: this.accountState.nickname,
      // M123: the storefront state (strip + Quick Race sub) + display price.
      ownsGame: this.titleOwnership(),
      gamePrice: GAME_PRICE,
    };
  }

  /** M101b: the owner-only dashboard (the title shows its entry only when
   *  the server-side is_admin flag rode in on the wallet). */
  private showAdmin(): void {
    this.leaveOnline();
    this.hideAll();
    audioEngine.setDuck(1);
    this.setMood('menu');
    this.attractMenu(GameState.Menu);
    this.adminScreen.show();
  }

  // ----- ONBOARDING (M101) --------------------------------------------------

  /** Front the post-signup welcome for a brand-new Racer ID. Checked when the
   *  account state changes AND whenever the title fronts (a sync may finish
   *  while another screen is up). Once per session; the server-side
   *  profiles.onboarded flag makes it once per ACCOUNT, ever. */
  private maybeStartOnboarding(): void {
    if (!this.account.enabled || this.onboardingHandled) return;
    // M123: a purchase in progress outranks the welcome — the name claim can
    // wait; a paying customer completes payment first. The intent clears on
    // every abandonment path (and on success), and this method re-runs on the
    // next title landing, so the welcome is deferred, never lost.
    if (this.purchaseIntent) return;
    const st = this.accountState;
    if (st.status !== 'signedIn' || st.onboarded !== false) return;
    // M123 (owner direction, 2026-08-14): the M101 device-progress grandfather
    // (hasMeaningfulProgress → silent markOnboarded) is REMOVED. Under
    // browse-first the PRIMARY buyer journey is "play the free teaser race,
    // then buy" — and a teaser race creates exactly the device progress the
    // grandfather keyed on, so it silently skipped onboarding for the very
    // customers it should welcome (the owner hit this live: his new buyer
    // account never saw the flow). The rule's original job — don't re-onboard
    // the legacy tester circle — is already served server-side: those accounts
    // carry onboarded=true in the database. Every account whose server-side
    // flag is false gets the welcome now; the demo race inside it stays
    // skippable, so a teaser veteran pays only the name claim they need
    // anyway (unique usernames).
    //
    // Only interrupt menu surfaces (sign-in only happens from them; a race
    // launched via dev params must never be yanked into a welcome).
    if (!this.title.isVisible && !this.accountScreen.isVisible) return;
    this.onboardingHandled = true;
    this.showOnboarding(false);
  }

  private showOnboarding(preview: boolean): void {
    this.onboardingPreview = preview;
    this.leaveOnline();
    this.hideAll();
    audioEngine.setDuck(1);
    this.setMood('menu');
    this.attractMenu(GameState.Menu);
    // Fully opaque over the world — skip the attract render (the M80 trick).
    this.game.setAttractRenderPaused(true);
    this.onboarding.show(this.save, {
      checkNames: (names) =>
        this.onboardingPreview
          ? Promise.resolve(Object.fromEntries(names.map((n) => [n, true])))
          : this.account.checkUsernames(names),
      claimName: async (name) => {
        if (this.onboardingPreview) return { ok: true, reason: '' };
        const res = await this.account.claimUsername(name);
        if (res.ok) {
          // The claimed username is the player's own word — it lives in the
          // save exactly like a title-screen rename (the M100 rule that only
          // USER-authored names enter the save is untouched).
          this.save.nickname = name;
          storeSave(this.save);
        }
        return res;
      },
      // M112: a claimed name drops the player straight into the demo race.
      onDemo: () => this.startOnboardingDemo(),
      shareData: () => ({
        code: this.accountState.referralCode ?? (this.onboardingPreview ? 'PREVIEW' : null),
        claimed: this.accountState.refClaimed,
        points: this.accountState.refPoints,
      }),
      onShare: () => this.shareReferral(),
      onRace: () => {
        this.closeOnboarding();
        this.showCitySelect();
      },
      onLearn: () => {
        this.closeOnboarding();
        this.showHowToPlay();
      },
      // M101b: the persistent SKIP exits from the finale.
      onExit: () => {
        this.closeOnboarding();
        this.showTitle();
      },
      // M101b: the mute toggle rides the whole flow (the title button's twin).
      onToggleMute: () => {
        this.save.settings.muted = !this.save.settings.muted;
        storeSave(this.save);
        this.game.applySettings(this.save.settings);
        return this.save.settings.muted;
      },
    });
  }

  private closeOnboarding(): void {
    this.onboarding.hide();
    this.game.setAttractRenderPaused(false);
  }

  /** M112/M114 — launch the SHARED demo race: a live solo NYC race that
   *  freezes under teaching popups (the rule → driving → the ball → collect +
   *  fire a boost), driven by DemoRaceDirector. ONE launcher serves the
   *  onboarding flow AND the HOW TO PLAY guide, so any future change to the
   *  onboarding demo (steps, copy, pacing) reaches the guide's demo
   *  automatically. The caller owns the exits (cb.onComplete / cb.onSkip);
   *  while `this.demoRace` lives, pause + raceFinished route to the director
   *  and the demo records/awards NOTHING — even a full drive to the line. */
  private runDemoRace(cb: DemoRaceDirectorCallbacks): void {
    this.hideAll();
    audioEngine.setDuck(1);
    this.mode = 'quick';
    // M123: the tutorial demo is NEVER a teaser race — it is uncapped and
    // ungated by construction (onRaceFinished returns at this.demoRace before
    // the counter; this clear is belt-and-braces for the flag itself).
    this.teaserRace = false;
    const city = CITIES[0]; // NYC: the starter course; first ? box row at arc 140
    this.setMood(city.id);
    this.game.loadCity(city, this.save.settings.difficulty, 1); // SOLO — clean track
    this.veil.flash(); // blink over the menu-surface → race-world swap
    this.game.beginRace();
    // No 3-2-1 — the demo opens FROZEN under the rule popup instead (the
    // countdown would tick against a frozen world and read broken).
    const access = this.game.tutorialAccess;
    if (access) {
      access.sim.state = 'racing';
      // Owner 2026-09-16: the first ? boxes move out to the second row so the
      // dribble lesson gets a real run before GRAB A ? BOX (demo only — the
      // world is rebuilt from tuning for every normal race).
      access.race.trimPickupsBefore(DEMO_FIRST_BOX_MIN_ARC);
    }
    this.demoRace = new DemoRaceDirector(this.game, this.parentEl, cb);
  }

  /** M112 — the onboarding DEMO RACE. Entered the moment the Racer ID name is
   *  claimed; the director hands control back here for the unlock video
   *  (complete) or the finale (skip). */
  private startOnboardingDemo(): void {
    // hide() (not closeOnboarding) restores the portrait rotate nudge — the
    // race is landscape-first and the nudge IS part of the lesson.
    this.onboarding.hide();
    this.game.setAttractRenderPaused(false);
    this.runDemoRace({
      onComplete: () => this.endOnboardingDemo(1),
      onSkip: () => this.endOnboardingDemo(2),
    });
  }

  /** Leave the demo race for the unlock video (1) or the finale (2). */
  private endOnboardingDemo(face: 1 | 2): void {
    this.demoRace?.dispose();
    this.demoRace = null;
    this.veil.flash(); // blink over the race → onboarding swap
    this.hideAll();
    audioEngine.setDuck(1);
    this.setMood('menu');
    this.attractMenu(GameState.Menu);
    this.game.setAttractRenderPaused(true); // the onboarding faces are opaque
    this.onboarding.reopen(this.save, face);
  }

  /** M114 — the HOW TO PLAY guide's demo race: the intro video hands off here
   *  (ended or tap-skipped). The SAME demo as onboarding by construction
   *  (runDemoRace); BOTH endings land back on the guide's chapters — the
   *  demo's own SKIP chip / Esc is the "straight to the written guide" path. */
  private startHowToPlayDemo(): void {
    this.howToPlayScreen.hide();
    this.runDemoRace({
      onComplete: () => this.endHowToPlayDemo(),
      onSkip: () => this.endHowToPlayDemo(),
    });
  }

  /** Leave the guide's demo for the text chapters. Unlike the onboarding exit
   *  this KEEPS the attract render running — the guide is a translucent panel
   *  over the attract world, not an opaque onboarding face. */
  private endHowToPlayDemo(): void {
    this.demoRace?.dispose();
    this.demoRace = null;
    this.veil.flash(); // blink over the race → guide swap
    audioEngine.setDuck(1);
    this.setMood('menu');
    this.showHowToPlay({ startAtChapters: true });
  }

  /** The share sheet with MY referral link (sharer-only rewards: 20 SP per
   *  friend who creates a Racer ID via it, capped at 5 — server-enforced). */
  private shareReferral(): Promise<'shared' | 'copied' | 'dismissed'> {
    const code = this.accountState.referralCode;
    if (!code) return Promise.resolve('dismissed');
    return shareReferralLink(code);
  }

  /** M114 — the title SHARE card's action: signed-in players share their
   *  referral link (the M101 sharer-only SP rewards ride it); guests share
   *  the plain canonical game link. One sheet, one copy voice (referral.ts). */
  private shareGame(): Promise<'shared' | 'copied' | 'dismissed'> {
    const code = this.accountState.referralCode;
    return code ? shareReferralLink(code) : shareGameLink();
  }

  /** ?debug=1 QA handle (window.__onboarding.preview()): run the whole flow
   *  on demand with NO server writes — layout eyeballing on any account. */
  debugOnboardingPreview(): void {
    this.onboardingHandled = true; // the real trigger must not double-front
    this.showOnboarding(true);
  }

  /** Repaint account-aware UI after save/state changes (coalesced — a sync
   *  pass writes the save several times back-to-back). */
  private refreshAccountUi(): void {
    if (this.accountUiQueued) return;
    this.accountUiQueued = true;
    window.setTimeout(() => {
      this.accountUiQueued = false;
      // M102c: an in-place corner swap — a full title.show() here cancelled
      // the bounce-in landing mid-flight (the owner's missing-animation bug).
      if (this.title.isVisible) this.title.refreshAccount(this.save, this.titleAccountInfo());
      this.accountScreen.refresh(this.save, this.accountState);
    }, 60);
  }

  /** M100: bank a race-shaped award by the routing rule — the account outbox
   *  (linked), the local save (kill switch / Big Kid), or the guest pot. */
  private bankRaceAward(
    place: number,
    kind: 'race' | 'tour_finale_online',
    eventId: string,
  ): { award: SkillAward; newlyUnlocked: string[]; guest: boolean } {
    const award = kind === 'race' ? raceSkillAward(place) : tourFinaleAward(place);
    const res = bankAward(this.save, award, { id: eventId, kind, place }, this.account.enabled);
    this.save = res.save;
    storeSave(this.save);
    if (res.route === 'account') this.account.scheduleSync();
    return { award: res.award, newlyUnlocked: res.newlyUnlocked, guest: res.route === 'guest' };
  }

  /** The guest card's context: the would-be payout + the pot it landed in. */
  private guestPending(award: SkillAward): { award: SkillAward; pot: number; potFull: boolean } {
    return {
      award,
      pot: this.save.guestPoints,
      potFull: this.save.guestPoints >= GUEST_POT_CAP,
    };
  }

  /** M79: publish the current ride's equipped cosmetics for the race view
   *  (offline player car). Call whenever the save's ride or loadout changes.
   *  M87: the serialized loadout rides along as the look KEY — the offline
   *  race rebuilds the player view when it changes (see carLook.ts). */
  private syncPlayerLook(): void {
    const veh = this.save.settings.playerVehicle;
    const loadout = loadoutFor(this.save, veh);
    // M109: the RIDE rides along too — the online path backstops our own seat
    // with this seam, and must only do so when it's this same car's loadout.
    setPlayerLook(resolveLook(loadout), serializeLoadout(loadout), veh);
  }

  /** M81: a vehicle's equipped cosmetics as the wire string carried on join
   *  URLs + lobby messages, so every online client dresses our car. */
  private lookStringFor(vehicleId: string): string {
    return serializeLoadout(loadoutFor(this.save, vehicleId));
  }

  /** M110: the display name WE carry into an online room — the Racer ID
   *  username when signed in (the M103 identity authority; it's also mirrored
   *  into save.nickname, which covers a boot where the account hasn't resolved
   *  yet), a guest's typed nickname otherwise, '' when neither exists (rivals
   *  then see the deterministic pool handle, exactly as before M110). */
  private netDisplayName(): string {
    return sanitizeNetName(this.accountState.nickname || this.save.nickname);
  }

  /** Boot to the title — or, with dev URL params, straight into a race. */
  boot(
    dev: {
      cityId?: string;
      autostart?: boolean;
      net?: string;
      link?: Partial<LinkConfig>;
      wsUrl?: string;
      hostKey?: string;
      room?: string;
      field?: number;
      tour?: number;
      cities?: string;
    } = {},
  ): void {
    this.game.start();
    // A ?host= override (local testing) applies to menu-launched online too —
    // matchmaking included, so a local worker serves the whole flow.
    if (dev.wsUrl) {
      this.netBase = dev.wsUrl;
      this.mmBase = dev.wsUrl;
      this.devWsUrl = dev.wsUrl; // M140: crew rooms stay on it too
    } else if (dev.hostKey) {
      // V2 (M106): an invite link's ?h= pins the ROOM host by allowlist key
      // (matchmaking always stays on the main worker).
      const host = roomHostByKey(dev.hostKey);
      if (host) this.netBase = host.url;
    } else if (dev.room) {
      // V2 Phase B (M107): no explicit host on the link — the room code's own
      // region letter still routes correctly (older copied links, hand-typed
      // URLs). Legacy 4-char codes stay on the default host.
      const hk = hostKeyForRoomCode(dev.room.toUpperCase());
      const host = hk ? roomHostByKey(hk) : undefined;
      if (host) this.netBase = host.url;
    }
    const cityIdx = Math.max(
      0,
      CITIES.findIndex((c) => c.id === dev.cityId),
    );
    // M123 — BACK FROM STRIPE. The success URL lands with ?session_id=
    // (captured + stripped by AccountService at construction; the sync's
    // confirm-checkout step consumes it): front the paywall on its calm
    // CONFIRMING face instead of the title. The onState refresh flips it —
    // owned resolves true → the success face; ownership still false (webhook
    // slow) → it simply stays confirming while the sync retries. NEVER an
    // error for slowness.
    // M130 — back from an SP-PACK checkout (success or cancel both carry
    // &sp=): land in the SHOP, not on the base-game paywall faces. The credit
    // arrives via confirm-checkout / the webhook on the sync that is already
    // running; the wallet refresh repaints the BANK and the toast fires from
    // onState the moment purchasedPoints grows (spPackReturn below).
    if (this.account.checkoutSpPack) {
      if (this.account.returnedFromCheckout) {
        this.spPackReturn = {
          pack: this.account.checkoutSpPack,
          before: this.save.purchasedPoints,
        };
      }
      this.showShop();
      return;
    }
    if (this.account.returnedFromCheckout) {
      this.checkoutConfirming = true;
      this.showPurchase('return');
      return;
    }
    // M123 — a CANCELLED checkout (?checkout=cancelled, already stripped).
    // Nothing was charged; land on the paywall's plain pitch face, normally
    // dismissible back to the title. Deliberately not an error state.
    if (this.account.checkoutCancelled) {
      this.showPurchase('gate');
      return;
    }
    // M121 CHECKPOINT — the two boot paths that SKIP THE TITLE ENTIRELY.
    // `?city=tokyo` / `?autostart` starts any city, and `?net=ws&room=CODE`
    // joins an online room. Ungated, either one is the full game via a typed
    // URL. Both land on the TITLE instead — which under M123 browse-first is
    // exactly where a non-owner should land: the browsable storefront, with
    // the gate meeting them at the launch verbs.
    if ((dev.net === 'ws' || dev.cityId || dev.autostart) && this.purchaseGatePossible()) {
      this.showTitle();
      return;
    }
    if (dev.net === 'ws') {
      // M83 BIG KID: an invite link opened while the save is on the kids
      // difficulty lands on the title instead of joining — the PLAY ONLINE
      // card there reads OFF IN BIG KID MODE, which is the explanation.
      if (this.save.settings.difficulty === 'bigkid') {
        this.showTitle();
        return;
      }
      // An invite link (?room=CODE) joins that friend room; a bare ?net=ws is a
      // private vs-AI race on the live server. Config carried on the link rides
      // along so a friend-arrives-first join still sets the room up right (M45).
      if (dev.room) {
        const opts: OnlineConnectOpts = { racers: dev.field ?? 4 };
        if (dev.tour) {
          opts.tour = dev.tour;
          if (dev.cities) opts.tourCities = dev.cities.split(',').filter(Boolean);
        } else if (dev.cityId) opts.cityId = dev.cityId;
        // M63: an invite-link join walks straight into the race lobby — the
        // ride is picked (and cycled live) in there now.
        this.startOnline(dev.room, false, opts);
      } else {
        // Bare ?net=ws (dev solo vs the live referee) — default region.
        const host = this.pickCreateHost();
        this.netBase = host.url;
        this.startOnline(this.makeRoomCode(host.key), true);
      }
      return;
    }
    if (dev.net === 'loopback') {
      this.startLoopback(cityIdx, dev.link);
      return;
    }
    if (dev.cityId || dev.autostart) {
      this.startQuickRace(cityIdx);
      return;
    }
    // M121 QA — `?debug=1&buy=1` fronts the PURCHASE screen even while the
    // paywall flag is OFF. The owner's real-money end-to-end test needs a way
    // to reach BUY on the live build without gating anyone else; debug-gated
    // per the M100 crafted-link rule, and harmless regardless: the screen is
    // just a screen, the server refuses buyers who already own the game, and
    // payment happens on Stripe's page. The account layer resolves ~400 ms
    // after boot, and the onState refresh already repaints this screen live,
    // so the CTA flips from CREATE RACER ID to BUY on its own.
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('debug') === '1' && qs.get('buy') === '1') {
      this.showPurchase();
      return;
    }
    this.showTitle();
  }

  /** Phase 2 demo (`?net=loopback`): a local online race — authoritative host +
   *  predicting client over a simulated network, in this one tab. */
  private startLoopback(cityIdx: number, link?: Partial<LinkConfig>): void {
    this.mode = 'quick';
    this.teaserRace = false; // M123: dev loopback races never tick the cap
    this.hideAll();
    audioEngine.setDuck(1);
    this.setMood(CITIES[cityIdx].id);
    this.game.beginLoopbackRace(CITIES[cityIdx], this.fieldSize(), link);
  }

  // ----- Online (M30) ------------------------------------------------------

  /** The "PLAY ONLINE" hub: Challenge a Friend / (Quick Match, later). */
  /** V2 Phase B (M107): fire-and-forget latency probes to the regional room
   *  hosts — by the time a human finishes configuring a room (many seconds),
   *  the nearest region is known. An unreachable region is never picked. */
  private probeRoomHosts(): void {
    // M108b: re-probe if the last sweep is stale — a region that was briefly
    // unreachable (or a network that changed) must not be excluded for the
    // whole session.
    const now = Date.now();
    if (this.hostProbeStarted && now - this.hostProbeAt < 120_000) return;
    this.hostProbeStarted = true;
    this.hostProbeAt = now;
    for (const h of probeableRoomHosts()) {
      const t0 = performance.now();
      fetch(`${h.url.replace(/^wss:/, 'https:')}/healthz`, { cache: 'no-store' })
        .then((r) => {
          if (r.ok) this.hostLatency.set(h.key, performance.now() - t0);
        })
        .catch(() => {
          /* region unreachable — simply never picked */
        });
    }
  }

  /** Where a NEW friend/solo room is created: the lowest-latency probed
   *  region, or the default until (or unless) probes land. */
  private pickCreateHost(): RoomHost {
    let best: RoomHost | null = null;
    let bestMs = Infinity;
    for (const h of probeableRoomHosts()) {
      const ms = this.hostLatency.get(h.key);
      if (ms !== undefined && ms < bestMs) {
        best = h;
        bestMs = ms;
      }
    }
    return best ?? defaultRoomHost();
  }

  private showOnlineHub(): void {
    // M83 BIG KID: no online play at the kids difficulty. The title's locked
    // card is the normal gate — this guard covers every other route in.
    if (this.save.settings.difficulty === 'bigkid') {
      this.showTitle();
      return;
    }
    if (this.blockSuspended()) return;
    // M121: online is the SERVER-side cost, so it must never leak to a
    // non-owner. Owner round (2026-10-02): the hub itself OPENS for them,
    // wearing the lock + BUY THE GAME banner; every action on it is guarded
    // (onlineGuard in the constructor) and routes to the purchase screen.
    const lock = this.paywallLock('online');
    if (!lock) this.probeRoomHosts(); // V2: know the nearest region before a room is made
    this.leaveOnline();
    this.hideAll();
    audioEngine.setDuck(1);
    this.setMood('menu');
    this.attractMenu(GameState.Menu);
    this.onlineScreen.show(lock);
  }

  /** "Challenge Friends" → pick a quick race or a tour, then create the invite. */
  private showOnlineSetup(): void {
    this.hideAll();
    audioEngine.setDuck(1);
    this.setMood('menu');
    this.attractMenu(GameState.Menu);
    this.onlineSetupScreen.show(this.onlineSetup);
  }

  /** Host "Create Invite" from the setup screen → a quick-race or a tour room. */
  private createFriendRoom(cfg: OnlineSetupConfig): void {
    this.onlineSetup = cfg;
    const opts: OnlineConnectOpts =
      cfg.mode === 'tour'
        ? { racers: cfg.racers, tour: cfg.tourLen, tourCities: cfg.tourCities }
        : { racers: cfg.racers, cityId: cfg.cityId };
    // V2 Phase B: the room is created on the creator's nearest region; the
    // invite link (&h=) and the code's region letter route friends there.
    const host = this.pickCreateHost();
    this.netBase = host.url;
    this.startOnline(this.makeRoomCode(host.key), false, opts);
  }

  /** QUICK MATCH / START A TOUR (M44; search-in-lobby M64): the RACE LOBBY
   *  opens IMMEDIATELY in searching mode — the player cycles + locks a ride
   *  while the matchmaker's ~10 s window runs. Real humans light the seat
   *  columns as they're found; the matchmaker stages the remaining "joins"
   *  (the AI backfill — presented exactly like found players, never
   *  revealed). No separate matchmaking popup anymore. */
  private startMatchmaking(mode: MmMode): void {
    if (this.blockSuspended()) return;
    this.leaveOnline();
    this.mode = 'quick';
    this.tourIntermission.setCountsVisible(false); // never reveal the human count
    this.showRaceLobby({
      mode: 'matchmade',
      tour: mode === 'tour',
      fieldSize: MM_FIELD,
      info:
        mode === 'tour' ? this.tourInfo(MM_TOUR_LEN, MM_FIELD) : `Quick Match · ${MM_FIELD} racers`,
      roomKey: '', // learned at match time (enterMatched)
    });
    this.raceLobby.setSearching(true);
    this.matchmaking = new MatchmakingClient(this.mmBase, mode, {
      onQueue: (found) => this.raceLobby.setSearchCount(found),
      onMatch: (m) => this.onMatchFound(m),
      onError: () => {
        this.raceLobby.hide();
        this.lobbyScreen.showError('error');
      },
    });
  }

  /** Matched — connect to the assigned referee room IN PLACE (M64): the lobby
   *  stays up with the player's pick/lock intact; the roster then fills the
   *  seats (matched humans at once, the staged "players" with humanized
   *  delays). The room auto-starts once every racer locks in READY. */
  private onMatchFound(m: MmMatchMsg): void {
    this.matchmaking?.cancel();
    this.matchmaking = null;
    sfxCollect();
    // V2 Phase B (M107): the matchmaker names the regional room host — every
    // matched player gets the same one, so the room spawns near the players.
    this.netBase = roomHostByKey(m.h ?? '')?.url ?? defaultRoomHost().url;
    // Warm the race's assets NOW (landmarks/sky/textures) — they'll be cached
    // before the world builds, so entry is hitch-free (M52).
    warmCityAssets(m.tour ? CITIES[0].id : (m.city ?? CITIES[0].id));
    this.online = { room: m.room, vsAi: false, matchmade: true };
    this.onlineTour = null; // (re)set on the first tour welcome
    this.raceLobby.enterMatched({
      roomKey: m.room,
      info: m.tour
        ? this.tourInfo(m.tour, m.field)
        : this.roomInfo(m.city ?? CITIES[0].id, m.field),
    });
    let q = `?room=${encodeURIComponent(m.room)}&field=${m.field}&mmcap=${m.mmcap}`;
    if (m.tour) q += `&tour=${m.tour}`;
    else if (m.city) q += `&city=${encodeURIComponent(m.city)}`;
    // The lobby is the picker now — the join URL carries the CURRENTLY
    // highlighted ride (the player may have cycled during the search) and,
    // M81, that ride's equipped cosmetics.
    const veh = this.raceLobby.currentVehicle();
    q += `&veh=${encodeURIComponent(veh)}&look=${encodeURIComponent(this.lookStringFor(veh))}`;
    q += `&name=${encodeURIComponent(this.netDisplayName())}&sv=${SIM_VERSION}`; // M110
    this.game.connectWsRace(`${this.netBase}${q}`);
  }

  /** Connect to the live referee. `vsAi` ⇒ a private 1-human room that starts at
   *  once; otherwise a friend room (quick race or tour) — the RACE LOBBY (M63)
   *  opens with your ride rotating, the shareable code + link, and the grid
   *  filling live. The world is built from the server's welcome. */
  private startOnline(room: string, vsAi: boolean, opts?: OnlineConnectOpts): void {
    if (this.blockSuspended()) return;
    warmCityAssets(
      opts?.tour ? (opts.tourCities?.[0] ?? CITIES[0].id) : (opts?.cityId ?? CITIES[0].id),
    );
    this.mode = 'quick';
    this.online = { room, vsAi, matchmade: false, opts };
    this.onlineTour = null; // a fresh online session; (re)set on the first tour welcome
    this.tourIntermission.setCountsVisible(true); // friend tours show the ready count
    if (vsAi) {
      this.hideAll();
      audioEngine.setDuck(1);
      this.setMood('menu');
      this.lobbyScreen.showConnecting();
    } else {
      this.showRaceLobby({
        mode: 'friend',
        tour: !!opts?.tour,
        fieldSize: opts?.racers, // a code-join doesn't know yet — the welcome fills it in
        code: room,
        link: this.inviteLink(room, opts),
        info: opts ? this.lobbyInfo(opts) : '',
        roomKey: room,
      });
    }
    this.game.connectWsRace(this.roomWsUrl(room, vsAi, opts));
  }

  /** M63: JOIN WITH CODE from the online hub — a bare join that inherits the
   *  creator's room config (city/field arrive with the welcome). */
  private joinByCode(code: string): void {
    // V2 Phase B: the code's region letter pins the host the room lives on
    // (legacy 4-char codes → the default, consistently on every client).
    const hk = hostKeyForRoomCode(code);
    this.netBase = (hk ? roomHostByKey(hk) : undefined)?.url ?? defaultRoomHost().url;
    this.startOnline(code, false, undefined);
  }

  // ----- RACE CREW (M140) -----------------------------------------------------

  /** RACE CREW — reached from PLAY ONLINE and the Racer ID dashboard (owner
   *  rule). A web non-owner sees it under the BUY THE GAME banner (crews are
   *  for game owners); a signed-out owner signs in first (crews live on a
   *  Racer ID). Never in Big Kid. */
  private showRaceCrew(
    from?: 'online' | 'dashboard',
    opts: { tab?: CrewTab; welcome?: string } = {},
  ): void {
    if (this.save.settings.difficulty === 'bigkid' || !SUPABASE_ENABLED) {
      this.showTitle();
      return;
    }
    if (from) this.crewBack = from;
    if (this.blockSuspended()) return;
    const lock = this.paywallLock('crew');
    if (!lock && this.accountState.status !== 'signedIn') {
      this.showAccount(false, true);
      return;
    }
    if (!lock) this.probeRoomHosts(); // a crew race picks the nearest region
    this.leaveOnline();
    this.hideAll();
    audioEngine.setDuck(1);
    this.setMood('menu');
    this.attractMenu(GameState.Menu);
    this.raceCrew.show({ tab: opts.tab, lock });
    // The HQ photo covers the world — skip rendering the attract race under it.
    this.game.setAttractRenderPaused(true);
    if (opts.welcome) this.raceCrew.welcomeToCrew(opts.welcome);
  }

  /** START CLUB RACE (M142: the starter's CALL YOUR CLUB choice — a quick
   *  race in a picked or random city, or a 3/5/9-stop tour; club only or
   *  open): pick the room (nearest region), record the invite (the server
   *  tells the whole club live + by push), walk in. A 'random' city is rolled
   *  HERE, never sent; a tour still records a real (random) city because the
   *  invite row needs one, but the room rolls its own route. */
  private async startCrewRace(choice: CrewRaceChoice): Promise<string | null> {
    if (this.purchaseGateArmed()) {
      this.showPurchase('gate', 'crew');
      return null;
    }
    const pick = resolveCrewRaceChoice(choice);
    const host = this.pickCreateHost();
    const res = await this.social.startCrewRace({
      visibility: pick.visibility,
      room: this.makeRoomCode(host.key),
      host: host.key,
      city: pick.city,
      field: CREW_RACE_FIELD,
      tour: pick.tour,
    });
    if (res.status === 'open' && res.race) {
      // Someone in the crew opened one a moment ago — race together.
      this.joinCrewRace(res.race);
      return null;
    }
    if (res.status === 'rate') return 'Lots of club races just now. Try again in a few minutes.';
    if (res.status === 'not_in_crew') return 'Join or start a club first.';
    if (res.status !== 'ok' || !res.race) {
      return pick.tour > 0
        ? 'Could not start the club tour. Check your connection.'
        : 'Could not start the club race. Check your connection.';
    }
    void this.social.pushCrewRace(res.race.id); // iPhones in the crew get a ping
    this.enterCrewRoom(res.race);
    return null;
  }

  /** Walk into a crew race (the pop-up's JOIN, the crew screen, a push tap). */
  private joinCrewRace(race: CrewRaceInvite): void {
    if (this.save.settings.difficulty === 'bigkid') return;
    if (this.blockSuspended()) return;
    if (this.purchaseGateArmed()) {
      this.showPurchase('gate', 'crew');
      return;
    }
    this.crewCtl.markHandled(race.id);
    this.enterCrewRoom(race);
  }

  /** The crew race lobby: the room's config travels in the invite, so every
   *  member's join URL configures it identically (the M45 first-caller rule —
   *  whoever arrives first sets it up). M142: a club TOUR carries only its
   *  length (&tour=N, no city, no picks): the room rolls a random route, and
   *  every member's URL says the same thing. */
  private enterCrewRoom(race: CrewRaceInvite): void {
    this.leaveOnline();
    this.crewCtl.toast.hideAll();
    // Local testing (a dev origin's ?host=) keeps the crew room local too —
    // a shipped build never has devWsUrl (devHostOverrideAllowed, M108b).
    this.netBase = this.devWsUrl ?? roomHostByKey(race.host)?.url ?? defaultRoomHost().url;
    const crew = race.visibility === 'public' ? 'pub' : 'priv';
    const tour = race.tourLen > 0;
    const opts: OnlineConnectOpts = tour
      ? { racers: race.field, tour: race.tourLen, crew }
      : { racers: race.field, cityId: race.city, crew };
    // A tour's first city is unknown until the welcome (onNetBegin warms it).
    if (!tour) warmCityAssets(race.city);
    this.mode = 'quick';
    this.online = { room: race.room, vsAi: false, matchmade: false, opts, crew: race };
    this.onlineTour = null; // (re)set on the first tour welcome
    // An OPEN club tour's intermission must not count the humans (the
    // backfill is presented exactly like found racers — the M44 rule, as in
    // matchmade tours); a club-only tour shows its ready count like friends.
    this.tourIntermission.setCountsVisible(race.visibility !== 'public');
    this.showRaceLobby({
      mode: 'crew',
      crewName: race.crewName,
      crewPublic: race.visibility === 'public',
      tour,
      fieldSize: race.field,
      info: clubLobbyInfo(race),
      roomKey: race.room,
    });
    this.game.connectWsRace(this.roomWsUrl(race.room, false, opts));
  }

  /** The lobby's start button: FIND RACERS in an open crew race (the room
   *  opens its empty seats; we hold the matchmaker anchor that routes this
   *  region's Quick Match searchers in), START everywhere else. */
  private onLobbyStart(): void {
    const crew = this.online?.crew;
    if (crew && crew.visibility === 'public') {
      // The anchor opens when the room confirms the search (netRoster).
      this.crewSearchPressed = true;
      this.game.sendCrewSearch();
      return;
    }
    this.game.startOnlineNow();
  }

  /** The lobby's back chevron: a crew race returns to RACE CREW. */
  private onLobbyLeave(): void {
    if (this.online?.crew) this.showRaceCrew();
    else this.showOnlineHub();
  }

  /** Stop offering this crew race to the rest of the crew (it started, its
   *  seats closed, or its last racer left). Once per race, any member may. */
  private closeCrewInvite(): void {
    const crew = this.online?.crew;
    if (!crew || this.crewRacesClosed.has(crew.id)) return;
    this.crewRacesClosed.add(crew.id);
    void this.social.closeCrewRace(crew.id);
  }

  /** Where the player is, for the crew invite pop-up: a menu (pop it now), a
   *  race (the side message only — never a tap target mid-race), or a flow
   *  that must not be interrupted. The online lobby counts as a menu (the
   *  world is already "racing" behind it, so game.state alone would lie). */
  private crewMoment(): CrewMoment {
    if (this.demoRace || this.onboarding.isVisible || this.purchaseScreen.isVisible)
      return 'blocked';
    if (this.accountScreen.isVisible && this.accountState.status !== 'signedIn') return 'blocked';
    if (this.raceIntro.isVisible || this.globeActive) return 'racing';
    if (this.online && this.onlineTour) return 'racing'; // mid online tour: friends are waiting
    if (this.raceLobby.isVisible || this.results.isVisible || this.lobbyScreen.isVisible)
      return 'menu';
    const st = this.game.state;
    const inRace =
      st === GameState.Racing ||
      st === GameState.Countdown ||
      st === GameState.Paused ||
      st === GameState.Globe;
    return inRace ? 'racing' : 'menu';
  }

  private async copyText(text: string): Promise<boolean> {
    try {
      if (nativeShims.copyText) await nativeShims.copyText(text);
      else await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }

  /** Open the M63 race lobby (shared by friend create/join + matchmade). */
  private showRaceLobby(opts: Omit<RaceLobbyShowOpts, 'vehicles' | 'initialVehicle'>): void {
    this.hideAll();
    audioEngine.setDuck(1);
    this.setMood('menu');
    this.attractMenu(GameState.Menu);
    this.lobbyLocked = null;
    this.raceLobby.show({
      ...opts,
      vehicles: this.unlockedVehicles(),
      initialVehicle: this.save.settings.playerVehicle || 'coupe',
    });
  }

  /** The rides this player may bring online (garage order, M62 unlocks). */
  private unlockedVehicles(): string[] {
    return VEHICLE_ORDER.map((v) => v.id).filter((id) => isVehicleUnlocked(this.save, id));
  }

  /** M63: the grid is set — the server is holding its staging beat, so play the
   *  race intro (city splash / World Tour Cup globe) over it. Also arms the
   *  one-per-race online lifetime-points latch. */
  private onNetBegin(b: GameEvents['netBegin']): void {
    // M140: the crew race is under way — its invite stops being offered.
    if (this.online?.crew) this.closeCrewInvite();
    // Quick races award on the local finish; TOUR races award from the official
    // server result instead (onNetRaceResult) — don't arm the quick latch there.
    this.onlineAwardArmed = b.tourLen === 0;
    // M100: one idempotency key per online quick race (finish + cut-off share
    // it — the server can only ever bank this race once).
    this.onlineRaceEventId = b.tourLen === 0 ? `q:${crypto.randomUUID()}` : '';
    // M143: a newly ANNOUNCED race starts with a clean payout slate. (This used
    // to be wiped at every race start — which also fires on a mid-race rejoin,
    // erasing the payout card of a finish that was already banked.)
    this.pendingOnlineUnlocks = [];
    this.pendingOnlineAward = null;
    this.pendingGuestAward = null;
    if (b.raceNum <= 1) this.tourFinalRace = null; // a fresh online tour/race
    warmCityAssets(b.city || CITIES[0].id);
    // M89: the DESTINATION city's song starts fading in over the staging beat
    // (intro splash / World Tour Cup screen) and continues into the race —
    // one unbroken piece of music from the loading screen through the finish.
    this.setMood(b.city || 'menu');
    // Loading round: the splash shows the online warm-up's progress (started
    // at the welcome) — usually already READY by now.
    const loading = !!this.onlineWarm && !this.onlineWarm.ready;
    if (b.tourLen > 0) {
      this.raceIntro.showTour(
        b.tourLen,
        b.raceNum,
        this.lastFieldSize,
        undefined,
        undefined,
        loading,
      );
    } else {
      this.raceIntro.showCity(b.city, { racers: this.lastFieldSize, loading });
    }
    if (this.onlineWarm) {
      this.raceIntro.setProgress(this.onlineWarm.frac, this.onlineWarm.label);
      if (this.onlineWarm.ready) this.raceIntro.setReady();
    }
  }

  /** Loading round: warm the freshly-built online world during the lobby.
   *  Fire-and-forget; the result only steers the splash's progress/READY. */
  private startOnlineWarmup(): void {
    if (this.game.worldWarm) {
      this.onlineWarm = { frac: 1, label: 'READY', ready: true };
      return;
    }
    this.onlineWarm = { frac: 0.35, label: 'LOADING LANDMARKS', ready: false };
    void this.game
      .warmUpRace((p) => {
        this.onlineWarm = {
          frac: 0.35 + 0.65 * p.frac,
          label: Flow.warmLabel(p.phase),
          ready: false,
        };
        if (this.raceIntro.isVisible)
          this.raceIntro.setProgress(this.onlineWarm.frac, this.onlineWarm.label);
      })
      .then((r) => {
        if (!r.ok && r.shaders === 'cancelled') return; // a newer world took over
        this.onlineWarm = { frac: 1, label: 'READY', ready: true };
        if (this.raceIntro.isVisible) this.raceIntro.setReady();
      })
      .catch((e) => {
        reportError('online.warmup', e);
        this.onlineWarm = { frac: 1, label: 'READY', ready: true };
        if (this.raceIntro.isVisible) this.raceIntro.setReady();
      });
  }

  /** "City · N racers" / "Tour · K cities · N racers" for the lobby header. */
  private lobbyInfo(opts: OnlineConnectOpts): string {
    return opts.tour
      ? this.tourInfo(opts.tour, opts.racers)
      : this.roomInfo(opts.cityId ?? CITIES[0].id, opts.racers);
  }
  private roomInfo(cityId: string, racers: number): string {
    const city = CITIES.find((c) => c.id === cityId);
    return `${city ? city.name : cityId} · ${racers} racers`;
  }
  private tourInfo(len: number, racers: number): string {
    return `Tour · ${len} cities · ${racers} racers`;
  }

  /** Results "REMATCH": re-race the same people on the open connection (the server
   *  waits until everyone's ready), reusing the lobby for the wait. */
  private onRaceAgainPressed(): void {
    if (!this.online) {
      this.restartRace();
      return;
    }
    this.results.hide();
    audioEngine.setDuck(1);
    this.setMood('menu');
    // Matchmade rooms have no invite link — a rematch just waits for whoever
    // stayed (leavers' slots are AI, indistinguishable as ever).
    // M142: an OPEN club race reads like a matchmade one (its seat counts
    // would reveal the backfill).
    if (this.online.vsAi || this.online.matchmade || this.online.crew?.visibility === 'public')
      this.lobbyScreen.showConnecting('REMATCH');
    else
      this.lobbyScreen.showInvite({
        code: this.online.room,
        link: this.inviteLink(this.online.room, this.online.opts),
        rematch: true,
      });
    // The host could always force the rematch vs AI server-side ({t:'start'}),
    // but the button only used to appear on a welcome — surface it here too.
    this.lobbyScreen.setCanStart(this.game.localSlot === 0);
    this.game.rematchOnline();
  }

  /** The race actually started (first server snapshot) — drop any waiting screens.
   *  For a tour this also resolves the globe transition into the next race. */
  private onNetRaceStart(): void {
    // The globe-flight → race swap is a hard renderer cut — blink over it (M65).
    if (this.globeActive) this.veil.flash();
    this.endOnlineGlobeCleanup();
    this.tourResultShown = false; // a fresh race — its result hasn't landed yet
    this.onlineResultShown = false; // M75: quick-race results not shown yet
    // M143: a quick race we were here for, but whose 'begin' was sent while our
    // socket was down, is armed HERE instead — once (Game hands this out one
    // time per race). It used to pay nothing: only 'begin' armed the latch.
    if (this.game.takeMissedBegin() && this.online && !this.onlineTour) {
      this.onlineAwardArmed = true;
      this.onlineRaceEventId = `q:${crypto.randomUUID()}`;
      this.pendingOnlineUnlocks = [];
      this.pendingOnlineAward = null;
      this.pendingGuestAward = null;
    }
    this.lobbyScreen.hide();
    this.raceLobby.hide();
    // The race INTRO may now fade — the live countdown is underneath (M65:
    // its fade is gated on this, so the reveal never lands on a stale lobby).
    this.raceIntro.raceReady();
    this.results.hide();
    this.tourIntermission.hide();
    // If the connection dropped while PAUSED, the auto-rejoin lands here with
    // the overlay still up over a fresh race — clear it.
    this.pauseScreen.hide();
    // Online races play the CITY's groove, like offline (was stuck on the menu
    // marimba — the mood was only ever set while screens were up).
    audioEngine.setDuck(1);
    this.setMood(this.game.currentCity.id);
  }

  /** M143c: the room's own list of this tour's results arrived with a
   *  welcome. Add a row for every race we have none for — a result that went
   *  out while our connection was down (we came back after the NEXT race had
   *  begun, so nothing re-sent it). Without this the championship stayed one
   *  race short to the end of the tour: a wrong table, a wrong podium, a
   *  wrong finale bonus. When this page was in the tour for those races
   *  (`payable`: a rejoin, not a reload), their rewards are paid too — under
   *  the same per-race key as always, so the account server can never count
   *  one twice. No screen changes here: a race is about to run or running. */
  private onNetTourSync(t: GameEvents['netTourSync']): void {
    const tour = this.onlineTour;
    if (!tour) return;
    const room = this.online?.room ?? 'room';
    for (let i = tour.results.length; i < t.results.length; i++) {
      const r = t.results[i];
      tour.results.push({
        cityId: r.city || this.game.currentCity.id,
        places: r.places,
        timeMs: null,
      });
      if (!t.payable) continue;
      const bank = this.bankRaceAward(r.places[t.slot] ?? 0, 'race', `t:${room}:${i + 1}:r`);
      if (!bank.guest && bank.award.total > 0) {
        this.spToast.show(
          bank.award.total,
          bank.newlyUnlocked[0] ? vehicleLabel(bank.newlyUnlocked[0]) : undefined,
        );
      }
    }
  }

  /** A tour race finished — record the official result, then either ready-up for
   *  the next city (intermission) or show the championship summary (the finale). */
  private onNetRaceResult(r: GameEvents['netRaceResult']): void {
    this.onlineRacersForReport(); // M156: remember who raced, before teardown
    if (!this.onlineTour) this.onlineTour = { len: r.tourLen, results: [] };
    // M143: `repeat` = this race's result was already recorded and this message
    // only adds that it closed the tour (a server shutting down ends a tour
    // early). Exact re-sends never reach here (Game drops them). So a repeat
    // records nothing and pays nothing again — the championship used to count
    // that race twice.
    const firstReport = !r.repeat;
    this.tourResultShown = true; // official result is in — the waiting state stands down
    // M75: end the on-track view whatever it is — spectating, the skipped waiting
    // card, OR still driving as the cut-off last car (races now end at the
    // 2nd-to-last racer, so a tour player can be cut off mid-drive).
    this.game.endRaceView();
    if (firstReport) {
      this.onlineTour.results.push({
        cityId: this.game.currentCity.id,
        places: r.places,
        timeMs: null,
      });
    }
    // M63: ONLINE tour races award Skill Points too — from the OFFICIAL
    // server places (once per race), never the provisional local standings.
    // M78: the payout is a full SkillAward (podium bonus included); the tour
    // intermission has no results card, so a toast pops the "+N".
    let raceGain = 0;
    let raceUnlocked: string[] = [];
    let raceGuestAward: SkillAward | null = null; // M100: pot-routed payout
    let raceAward: SkillAward | null = null; // M158c: the race card's payout
    if (firstReport) {
      const myPlace = r.places[this.game.localSlot ?? 0] ?? 0;
      // M100: deterministic per (room, race) — a reload+rejoin re-delivering
      // this result can re-run the client math, but the server dedupes it.
      const room = this.online?.room ?? 'room';
      const bank = this.bankRaceAward(myPlace, 'race', `t:${room}:${r.raceNum}:r`);
      raceGain = bank.guest ? 0 : bank.award.total;
      raceUnlocked = bank.newlyUnlocked;
      if (bank.guest && bank.award.total > 0) raceGuestAward = bank.award;
      if (!bank.guest && bank.award.total > 0) raceAward = bank.award;
    }
    audioEngine.setDuck(0.3);
    const { table, champion, trophies } = this.onlineChampionship();
    if (r.last) {
      // M78: a top-3 championship pays its bonus once, revealed on the summary.
      let bonus: SkillAward | null = null;
      let bonusUnlocked: string[] = [];
      let bonusGuestAward: SkillAward | null = null; // M100
      if (firstReport) {
        const champPlace = table.findIndex((row) => row.isPlayer) + 1;
        const room = this.online?.room ?? 'room';
        const bank = this.bankRaceAward(
          champPlace,
          'tour_finale_online',
          `t:${room}:${r.raceNum}:f`,
        );
        if (bank.award.total > 0) {
          if (bank.guest) bonusGuestAward = bank.award;
          else {
            bonus = bank.award;
            bonusUnlocked = bank.newlyUnlocked;
          }
        }
      }
      // M94: stash the final race's OFFICIAL stats (server places over the
      // local standings order) for the summary's FINAL RACE STATS toggle.
      const myPlace = r.places[this.game.localSlot ?? 0] ?? 0;
      const cur = this.game.currentResult(undefined, r.times); // official times (M143c)
      const finalEntries = cur.entries
        .map((e) => ({ ...e, place: r.places[e.racerId] ?? e.place }))
        .filter((e) => e.place > 0)
        .sort((a, b) => a.place - b.place);
      this.tourFinalRace = {
        mode: 'tour',
        cityName: this.game.currentCity.name,
        entries: finalEntries,
        playerPlace: myPlace,
        playerTimeMs: finalEntries.find((e) => e.isPlayer)?.timeMs ?? null,
        isNewBestTime: false,
        tourRace: r.raceNum,
        tourTotal: r.tourLen,
        isLastTourRace: true,
        online: true,
        reducedMotion: this.save.settings.reducedMotion,
      };
      // M100: a guest's tour payout (race + any podium bonus went to the pot)
      // renders as the guest card on the summary.
      const guestAward = bonusGuestAward ?? raceGuestAward;
      const summary = {
        table,
        trophies,
        champion,
        championshipBonus: bonus,
        spTotal: this.save.lifetimePoints,
        spBank: spBalance(this.save),
        grantedVehicles: this.save.unlockedVehicles,
        newlyUnlocked: [...raceUnlocked, ...bonusUnlocked],
        reducedMotion: this.save.settings.reducedMotion,
        guestPending: guestAward ? this.guestPending(guestAward) : undefined,
        online: true,
      };
      const showSummary = (quiet: boolean): void => {
        // M96: the Champion song keeps scoring the standings at full level.
        if (!quiet) {
          if (champion) sfxChampion();
          else sfxFanfare(2);
        }
        if (firstReport && raceGain > 0) {
          this.spToast.show(raceGain, raceUnlocked[0] ? vehicleLabel(raceUnlocked[0]) : undefined);
        }
        this.results.showTourSummary(summary, this.tourFinalRace ?? undefined);
      };
      this.hideAll();
      this.attractMenu(GameState.Menu);
      // M96: the finale is scored by the CHAMPION song (cinematic + standings,
      // one unbroken piece; anthem fallback if the file ever fails).
      this.setMood('champion');
      // M94: EVERY racer sees the podium celebration before the standings —
      // each client plays it locally off the official final result. A
      // duplicate result message (mid-race rejoin re-send) skips the replay.
      if (!firstReport) {
        showSummary(true);
        return;
      }
      audioEngine.setDuck(1); // the celebration scores at full music
      void this.playTourCelebration(table).then(({ played, congrats }) => {
        if (played) showSummary(congrats);
      });
    } else {
      // M158c (owner, "two steps"): the race's own results card first (cup,
      // time, rewards), then TOUR STANDINGS = the intermission screen with
      // READY. The server's fallback clock runs across both steps.
      const slot = this.game.localSlot ?? 0;
      const place = r.places[slot] ?? 0;
      sfxFanfare(place || 4);
      this.hideAll();
      const nextCityAt = Date.now() + COUNTDOWN_SECONDS * 1000;
      const results = this.onlineTour.results;
      const stops: TourStop[] = Array.from({ length: r.tourLen }, (_, i) => {
        const res = results[i];
        if (!res) return { name: null, place: null };
        return {
          name: CITIES.find((c) => c.id === res.cityId)?.name ?? null,
          place: res.places[slot] ?? null,
        };
      });
      const prevTable = this.onlineChampionship(results.length - 1).table;
      const cur = this.game.currentResult(undefined, r.times);
      const entries = cur.entries
        .map((e) => ({ ...e, place: r.places[e.racerId] ?? e.place }))
        .filter((e) => e.place > 0)
        .sort((a, b) => a.place - b.place);
      const showStandings = (): void => {
        this.results.hide();
        this.tourIntermission.show({
          raceNum: r.raceNum,
          tourLen: r.tourLen,
          cityName: this.game.currentCity.name,
          playerPlaceTrophy: trophyForPlace(place),
          table,
          stops,
          prevTable,
          nextCityAt,
        });
      };
      if (!firstReport) {
        showStandings();
        return;
      }
      this.results.show({
        mode: 'tour',
        cityName: this.game.currentCity.name,
        entries,
        playerPlace: place,
        playerTimeMs: entries.find((e) => e.isPlayer)?.timeMs ?? null,
        isNewBestTime: false,
        newlyUnlocked: raceUnlocked,
        award: raceAward ?? undefined,
        guestPending: raceGuestAward ? this.guestPending(raceGuestAward) : undefined,
        spTotal: this.save.lifetimePoints,
        spBank: spBalance(this.save),
        grantedVehicles: this.save.unlockedVehicles,
        reducedMotion: this.save.settings.reducedMotion,
        tourRace: r.raceNum,
        tourTotal: r.tourLen,
        isLastTourRace: false,
        online: true,
        onTourStandings: showStandings,
        nextCityAt,
      });
    }
  }

  /** Tour transition: fly the globe from the city just raced to the next one. The
   *  SERVER starts the next race (a `restart` ~the globe duration later), so this
   *  is purely the visual hop — no client-side advance. */
  private onNetNextCity(n: GameEvents['netNextCity']): void {
    const from = CITIES.find((c) => c.id === n.from) ?? this.game.currentCity;
    const to = CITIES.find((c) => c.id === n.to) ?? CITIES[0];
    warmCityAssets(to.id); // the globe flight is ~6 s — plenty to cache the city
    audioEngine.setDuck(1);
    // M89: the NEXT city's song fades in with the globe flight and carries
    // straight into its race (song-less cities fade to the anthem instead).
    this.setMood(to.id);
    void this.startOnlineGlobe(from, to, n.raceNum, n.tourLen);
    // Loading round: pre-build the next stop's WORLD (meshes only — the
    // server's 'restart' welcome builds the NetRace on it) and warm it during
    // the flight, so the rebuild-at-restart + first-frames compile stalls are
    // gone for tour races 2+. The online difficulty is whatever this world
    // was built for (the server pins it).
    void this.game
      .loadCityAsync(to, this.game.currentDifficulty, this.lastFieldSize, undefined, {
        skipRace: true,
      })
      .then((ok) => (ok ? this.game.warmUpRace(undefined, { render: false }) : null))
      .catch((e) => reportError('online.tour.preload', e));
  }

  private async startOnlineGlobe(
    from: City,
    to: City,
    raceNum: number,
    tourLen: number,
  ): Promise<void> {
    this.hideAll();
    this.game.enterGlobe();
    this.globeScreen.show(from, to, raceNum, tourLen);
    this.globeActive = true;
    try {
      const { createGlobeFlight } = await import('@/world/Globe');
      if (!this.globeActive) return; // the race already started before the chunk loaded
      const flight = createGlobeFlight(from, to, CITIES);
      flight.onDone = () => {}; // online: the SERVER starts the next race (via restart)
      this.flight = flight;
      this.game.setGlobe(flight);
    } catch {
      /* globe chunk failed to load — the restart will switch us to the race anyway */
    }
  }

  /** Tear down a tour globe transition WITHOUT advancing (the server owns that). */
  private endOnlineGlobeCleanup(): void {
    if (!this.globeActive) return;
    this.globeActive = false;
    this.game.setGlobe(null);
    this.flight?.dispose();
    this.flight = null;
    this.globeScreen.hide();
  }

  /** The running championship for the online tour, centred on OUR slot (the pure
   *  championship marks slot 0 as "me"; online we may be any slot). */
  private onlineChampionship(upTo?: number): {
    table: ChampionshipRow[];
    champion: boolean;
    trophies: Trophy[];
  } {
    const all = this.onlineTour?.results ?? [];
    const results = upTo === undefined ? all : all.slice(0, Math.max(0, upTo));
    const slot = this.game.localSlot ?? 0;
    const table = championship(results);
    for (const row of table) {
      row.isPlayer = row.racerId === slot;
      // Online names: OUR slot is "You"; every other seat wears its REAL name
      // when the server relayed one, the pool handle otherwise (M44 / M110).
      row.name = this.game.onlineNameFor(row.racerId);
    }
    const champion = table.length > 0 && table[0].racerId === slot;
    const trophies = results.map((res) => trophyForPlace(res.places[slot] ?? 0));
    return { table, champion, trophies };
  }

  /** Close the online socket + clear the flags when leaving online for good. */
  private leaveOnline(): void {
    this.matchmaking?.cancel(); // leave any live search queue
    this.matchmaking = null;
    this.crewAnchor?.close();
    this.crewAnchor = null;
    this.crewSearchPressed = false;
    this.crewRoomTour = null;
    // M140: the last racer leaving a crew lobby closes its invite (the room
    // empties — nobody should be sent into it any more).
    const crew = this.online?.crew;
    if (crew && this.raceLobby.isVisible && this.rosterFilled.length <= 1) this.closeCrewInvite();
    if (this.online) {
      this.game.disconnectOnline();
      this.online = null;
    }
    this.onlineTour = null;
    this.onlineAwardArmed = false;
    this.lobbyLocked = null;
    this.endOnlineGlobeCleanup();
  }

  /** A short, unambiguous, lowercase-safe room code (no 0/O/1/I lookalikes).
   *  V2 Phase B: the 5th letter names the room's HOST region, so a typed code
   *  lands friends on the server the room actually lives on (lobby.ts). */
  private makeRoomCode(hostKey: string): string {
    const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
    const bytes = new Uint8Array(4);
    crypto.getRandomValues(bytes);
    let s = '';
    for (const b of bytes) s += alphabet[b % alphabet.length];
    return s + (ROOM_CODE_HOST_SUFFIX[hostKey] ?? '');
  }

  /** The ws URL the client connects to: a room, plus the host's config (city +
   *  field) for a friend room, or `solo` to size a private vs-AI room. A friend
   *  JOINING via an invite link carries no config and inherits the host's. */
  private roomWsUrl(room: string, vsAi: boolean, opts?: OnlineConnectOpts): string {
    let q = `?room=${encodeURIComponent(room)}`;
    if (vsAi) q += '&solo=1';
    else if (opts) {
      q += `&field=${opts.racers}`;
      if (opts.tour) {
        q += `&tour=${opts.tour}`;
        // M87: the host's ordered city picks — the room fills the rest randomly.
        if (opts.tourCities?.length)
          q += `&cities=${encodeURIComponent(opts.tourCities.join(','))}`;
      } else if (opts.cityId) q += `&city=${encodeURIComponent(opts.cityId)}`;
      if (opts.crew) q += `&crew=${opts.crew}`; // M140: a crew race room
    }
    // M51: ride + sim-version handshake; M81: the ride's equipped cosmetics;
    // M110: our display name (rivals show it above our car + in results).
    const veh = this.save.settings.playerVehicle || 'sports';
    q += `&veh=${this.vehicleParam()}&look=${encodeURIComponent(this.lookStringFor(veh))}`;
    q += `&name=${encodeURIComponent(this.netDisplayName())}&sv=${SIM_VERSION}`;
    return `${this.netBase}${q}`;
  }

  /** The shareable invite a friend opens to join (the game site, with ?net=ws).
   *  It CARRIES the room config (M45): if the friend somehow connects before the
   *  host, they configure the room identically instead of defaulting it — the
   *  same first-caller trick the matchmaker uses. Old bare links still work.
   *  M111: built on CANONICAL_ORIGIN, never the live origin — inside the app
   *  shell the live origin is capacitor://localhost (a dead link if shared),
   *  and referral links already follow this rule. Dev keeps local links so
   *  two-tab testing still works. */
  private inviteLink(room: string, opts?: OnlineConnectOpts): string {
    const base = import.meta.env.DEV
      ? `${location.origin}${location.pathname}`
      : `${CANONICAL_ORIGIN}/`;
    let url = `${base}?net=ws&room=${encodeURIComponent(room)}`;
    if (opts) {
      url += `&field=${opts.racers}`;
      if (opts.tour) {
        url += `&tour=${opts.tour}`;
        // M87: the picks ride the link too, so a friend-arrives-first join
        // still configures the room identically (the M45 first-caller trick).
        if (opts.tourCities?.length)
          url += `&cities=${encodeURIComponent(opts.tourCities.join(','))}`;
      } else if (opts.cityId) url += `&city=${encodeURIComponent(opts.cityId)}`;
    }
    // Carry the room's host so the friend lands on the SAME server: a chain
    // host travels as its allowlisted KEY (&h=, safe un-gated — V2 M106); a
    // local-dev override still travels as the raw &host= (debug-gated on the
    // receiving side). Links carry nothing when the room is on the default.
    const hostKey = roomHostKeyFor(this.netBase);
    if (this.netBase === defaultRoomHost().url) {
      // default — nothing to carry
    } else if (hostKey) {
      url += `&h=${encodeURIComponent(hostKey)}`;
    } else {
      url += `&host=${encodeURIComponent(this.netBase)}`;
    }
    return url;
  }

  // ----- Screens -----------------------------------------------------------

  /** M114b — attract mode behind a MENU screen. The ONE place swipe-to-go-back
   *  is re-armed: races call beginRace(), never this, so racing / paused /
   *  the tutorial demo can never take the gesture. */
  private attractMenu(state: GameState.Menu | GameState.CitySelect = GameState.Menu): void {
    this.game.enterAttract(state);
    setSwipeBackEnabled(true);
    // Loading round: the attract city is built in the background, in slices,
    // AFTER the menu has painted (two frames) — no-op once any build started.
    // Once it exists, warm it a beat later too (shaders/textures/post chain):
    // the first race is very often this same city (the NYC teaser / quick
    // race), and a warm world turns its loading splash into a 3 s beat.
    // Loading round 2: every slice of this is GATED on the player being quiet
    // (idleGate), and the warm-up starts only after a real pause in input —
    // its chunks are the biggest tasks the boot has (cold-cache shader
    // links), and they used to land under the first tap.
    void nextFrame()
      .then(nextFrame)
      .then(() => this.game.ensureWorldAsync(this.save.settings.difficulty, this.fieldSize()))
      .then(async (built) => {
        if (!built || this.bootWarmDone) return;
        this.bootWarmDone = true;
        await whenIdle(900, 6000);
        if (this.game.worldReady && !this.game.worldWarm) {
          await this.game.warmUpRace(undefined, { gated: true });
        }
      })
      .catch((e) => reportError('boot.world', e));
  }

  private hideAll(): void {
    this.settingsOpen = false;
    // M114b — swipe-to-go-back is DENY BY DEFAULT. Nothing is on screen at
    // this instant, and hideAll() runs before EVERY screen show AND before
    // every race start, so only attractMenu() (i.e. a menu) ever re-arms it.
    // That is layer (a) of the owner's "never swipe out of a race" rule; the
    // other layer is that a race has no visible back button to resolve.
    setSwipeBackEnabled(false);
    cancelSwipeBack();
    // A live finale cinematic resolves 'cancelled' — its caller must not
    // navigate over whatever is tearing the flow down (netClosed, leaving).
    this.podiumClip.cancel();
    // The welcome bubble drives off as soon as the player heads anywhere.
    this.welcomeToast?.hide();
    this.title.hide();
    this.citySelect.hide();
    this.garage.hide();
    this.results.hide();
    this.pauseScreen.hide();
    this.globeScreen.hide();
    this.settingsScreen.hide();
    this.onlineScreen.hide();
    this.onlineSetupScreen.hide();
    this.lobbyScreen.hide();
    this.raceLobby.hide();
    this.raceIntro.hide();
    this.leaderboardScreen.hide();
    this.howToPlayScreen.hide();
    this.onboarding.hide();
    this.adminScreen.hide();
    this.shopScreen.hide();
    this.accountScreen.hide();
    this.purchaseScreen.hide();
    this.tourIntermission.hide();
    this.raceCrew?.hide();
    closeSheet(); // a crew sheet never outlives the screen it opened over
    // Loading round: nothing opaque is up at this instant — un-pause the
    // attract render (onboarding.hide() above used to leave it paused; the
    // title/garage/shop re-pause it themselves), and any offline race launch
    // still building is superseded. (Meant to ship in M125 — the edit had
    // missed this branch's hideAll; landed in the loading round 2.)
    this.game.setAttractRenderPaused(false);
    this.raceLaunchGen++;
    // Loading round 2: a navigation is under way — background slices
    // (world build, compile chunks, model parsing) wait past the entrance
    // animation of whatever shows next.
    markBusy(700);
  }

  /** M78: WORLD RANKINGS — the Skill-Points leaderboard, off the title strip. */
  private showLeaderboard(): void {
    // Owner 2026-09-16: the board is parked (config/features.ts) — no surface
    // links here any more; this guard makes the screen unreachable even from
    // a stale caller until the flag flips back.
    if (!LEADERBOARD_ENABLED) return;
    this.leaveOnline();
    this.hideAll();
    this.attractMenu(GameState.Menu);
    // M100: guests see the practice ladder + the world-board push (never
    // advertised in Big Kid, where accounts don't exist by design).
    const cta =
      this.account.enabled &&
      this.accountState.status === 'signedOut' &&
      this.save.settings.difficulty !== 'bigkid'
        ? { onCreate: () => this.showAccount() }
        : null;
    this.leaderboardScreen.show(this.save, () => this.showTitle(), cta);
  }

  /** M86: HOW TO PLAY — the four-chapter guide, off the title menu card.
   *  M114: the guide's intro video hands off to the SAME demo race the
   *  onboarding runs (video → demo → chapters, every step skippable);
   *  `startAtChapters` is the post-demo return — straight to the text, no
   *  clip replay. */
  private showHowToPlay(opts?: { startAtChapters?: boolean }): void {
    this.leaveOnline();
    this.hideAll();
    this.attractMenu(GameState.Menu);
    this.howToPlayScreen.show(this.save, () => this.showTitle(), {
      onDemo: () => this.startHowToPlayDemo(),
      startAtChapters: opts?.startAtChapters ?? false,
    });
  }

  private setMood(mood: string): void {
    this.currentMood = mood;
    this.applyMood();
  }

  /** Route the current mood to a music source (M85/M89). REAL audio files own
   *  the menu screens (the anthem playlist — continues across screen hops,
   *  resumes after races) AND the races: a city mood plays that CITY's own
   *  song (started as early as the transition/intro screen, so it fades in
   *  there and simply continues through the race). A city with no song file
   *  races to the anthem instead (the default theme, owner rule). Fallback
   *  chain on file failure: city song → anthem → the synth groove — never
   *  silence. Re-run on audio unlock (the first gesture) so whatever screen
   *  is up starts its music. */
  private applyMood(): void {
    const mood = this.currentMood;
    if (mood === 'menu' || mood === 'transition') {
      if (soundtrack.playMenu()) music.stop();
      else music.play('menu');
      return;
    }
    // M96: the tour finale — the Champion song scores the podium cinematic
    // and the final standings (falls back to the anthem, then the synth).
    if (mood === 'champion') {
      if (soundtrack.playCelebration()) music.stop();
      else music.play('menu');
      return;
    }
    // A race mood (a city id): the city's song — or the anthem for song-less
    // cities. Only when neither file can play does the synth groove score the
    // race (the file decks fade themselves out under it — never a hard cut).
    if (soundtrack.playRace(mood)) {
      music.stop();
      return;
    }
    soundtrack.releaseToRace();
    music.play(mood);
  }

  private openSettings(returnTo: 'title' | 'pause'): void {
    this.settingsReturn = returnTo;
    this.settingsOpen = true;
    this.title.hide();
    this.pauseScreen.hide();
    // M102b: SIGN OUT shows only from the title (never mid-race — a pause
    // sign-out would yank the account layer under a live session).
    this.settingsScreen.show(
      this.save.settings,
      returnTo === 'title' && this.accountState.status === 'signedIn',
    );
  }

  private closeSettings(): void {
    this.settingsOpen = false;
    this.settingsScreen.hide();
    if (this.settingsReturn === 'pause') this.pauseScreen.show();
    else this.title.show(this.save, this.titleAccountInfo());
    // M111: settings is where SIGN OUT / DELETE live — landing back on the
    // title signed out inside the app shell re-fronts the boot gate.
    // (M123: the PAID gate no longer fronts here — a sign-out lands on the
    // browsable title; the gate meets them at the next launch verb.)
    this.maybeFrontAccountGate();
  }

  private showTitle(): void {
    this.leaveOnline();
    // M123: landing on the title abandons any purchase-in-progress (the flow
    // re-arms the moment they tap BUY / sign-in from the paywall again) and
    // closes the current teaser race's scope. Cleared BEFORE the onboarding
    // check below, so a deferred welcome fronts the moment the intent ends.
    this.setPurchaseIntent(false);
    this.teaserRace = false;
    this.hideAll();
    audioEngine.setDuck(1);
    this.setMood('menu');
    this.attractMenu(GameState.Menu);
    this.title.show(this.save, this.titleAccountInfo());
    // Loading round: a few seconds into an idle title, pre-create the garage's
    // WebGL context + IBL and warm the vehicle model cache (in yielding
    // slices) so the GARAGE tap — the reported "click didn't register" — is
    // instant. Once per session.
    if (!this.garagePrewarmed) {
      this.garagePrewarmed = true;
      // Loading round 2: only once the player has been quiet a while — it
      // is pure bonus and must never sit under a tap.
      window.setTimeout(() => {
        void whenIdle(1200, 12000).then(() => this.garage.prewarm());
      }, 2500);
    }
    // M101: a sync that finished while another screen was up parks the
    // pending welcome until the player is back on a menu — check here.
    this.maybeStartOnboarding();
    // M111: a signed-out return to the title re-fronts the app-shell gate.
    // (M123: the PAID gate deliberately does NOT front here any more — the
    // title is the browsable storefront; gating is on action, not arrival.)
    this.maybeFrontAccountGate();
    // M140: a crew/friend link waiting for a menu moment acts now.
    this.crewCtl.nudge();
  }

  private showCitySelect(): void {
    this.leaveOnline();
    this.hideAll();
    this.attractMenu(GameState.CitySelect);
    // Home-modernize round: a non-owner's grid shows the free teaser city
    // open and every other city as FULL GAME (tap = the paywall naming it).
    // Big Kid never sees commerce (M83) — its grid stays the plain tour-lock.
    this.citySelect.show(
      this.save,
      this.purchaseGateArmed() && this.save.settings.difficulty !== 'bigkid',
    );
  }

  /** M147 (owner): WORLD TOUR CUP for a resolved non-owner, signed in or
   *  not, shows the nine cities the tour races with the buy panel beside
   *  them — not the garage (the rides live behind the title's GARAGE card).
   *  Big Kid never sees commerce (M83): paywallLock is undefined there, so a
   *  non-owning Big Kid save keeps the plain garage + the launch-verb guard. */
  private showTourShowcase(lock: { price: string; onBuy: () => void; onLogIn?: () => void }): void {
    this.leaveOnline();
    this.hideAll();
    this.attractMenu(GameState.CitySelect);
    this.citySelect.showTourPromo({ price: lock.price, onBuy: lock.onBuy, onLogIn: lock.onLogIn });
  }

  /** Confirm/pick your ride in the garage before a tour (M35; showroom M68). */
  private showGarageForTour(): void {
    // M147: a non-owner gets the city showcase instead (above), so the garage
    // below is the owner's (and Big Kid's) — no lock banner or promo. The
    // tour's own launch verbs keep their hard guards regardless.
    const lock = this.paywallLock('tour');
    if (lock) {
      this.showTourShowcase(lock);
      return;
    }
    this.leaveOnline();
    this.hideAll();
    this.attractMenu(GameState.Menu);
    // Loading round: the garage/shop photo backdrop is opaque — don't render
    // the attract world under it (GPU the 3D stage needs, esp. on phones).
    this.game.setAttractRenderPaused(true);
    this.garage.show(this.save, {
      kicker: 'WORLD TOUR CUP',
      confirmLabel: 'START TOUR',
      onConfirm: (id) => {
        this.applyVehicle(id);
        this.garage.hide();
        this.beginNewTour();
      },
      onBack: () => this.showTitle(),
      guest: this.isGuestView(),
    });
  }

  /** The GARAGE from the title menu — browse/pick a ride, it persists, back to
   *  the menu. No race starts; every mode then launches with the saved ride. */
  private showGarage(): void {
    this.leaveOnline();
    this.hideAll();
    this.attractMenu(GameState.Menu);
    this.garageEditSpent = this.save.spentPoints; // M109 merge baseline
    // Loading round: the garage/shop photo backdrop is opaque — don't render
    // the attract world under it (GPU the 3D stage needs, esp. on phones).
    this.game.setAttractRenderPaused(true);
    this.garage.show(this.save, {
      kicker: 'GARAGE',
      confirmLabel: 'SAVE RIDE',
      onConfirm: (id) => {
        this.applyVehicle(id);
        this.showTitle();
      },
      onBack: () => this.showTitle(),
      onShop: (vehId) => this.showShop(vehId),
      // M87 CUSTOMIZE: equips persist immediately + the race look re-syncs.
      onSaveChange: (s) => {
        this.mergeGarageEdit(s);
        storeSave(this.save);
        this.syncPlayerLook();
        this.markGarageChanged(); // M109: equips follow the account
      },
      // M138 (owner): a non-owner's garage carries the lock banner (every
      // ride beyond the starter is earned by racing the full game) — the
      // confirm stays SAVE RIDE; signed out, XP / SP are hidden.
      lock: (() => {
        const l = this.paywallLock('garage');
        return l ? { ...l, confirmBuys: false } : undefined;
      })(),
      guest: this.isGuestView(),
      // M139: signed out → the showroom promo (every ride cycles onto the
      // turntable under the big buy panel; CUSTOMIZE opens its promo).
      promo: this.promoView()
        ? {
            // Owner rule (2026-10-02): buying the game does NOT unlock the
            // rides — racing does. The copy must never promise otherwise.
            // M147 (owner): the headline is the rule itself, and the sub is
            // gone so the one-purchase promise gets the room.
            kicker: 'THE GARAGE',
            headline: 'WIN RACES TO UNLOCK RIDES',
            onCustomize: (vehId: string) => this.showShop(vehId, 'customize'),
          }
        : undefined,
    });
  }

  /** M79: THE SHOP — spend Skill Points on cosmetics (from the title garage). */
  /** M91: `vehicleId` = the garage's browsed ride — the shop dresses THAT car
   *  (it used to always target the saved ride, so shopping "for" a browsed
   *  car silently equipped the wrong one — the owner's skins report). */
  private showShop(vehicleId?: string, focus: 'shop' | 'customize' = 'shop'): void {
    // M121: the shop is content behind the paywall. Owner round (2026-10-02):
    // a non-owner can still OPEN it to browse + preview (gameLock below), and
    // every BUY becomes BUY THE GAME. Big Kid wears no commerce banner (M83),
    // so a non-owning Big Kid save keeps the old hard gate.
    if (this.purchaseGateArmed() && this.save.settings.difficulty === 'bigkid') {
      this.showPurchase('gate', 'shop');
      return;
    }
    this.hideAll();
    this.attractMenu(GameState.Menu);
    this.garageEditSpent = this.save.spentPoints; // M109 merge baseline
    // Loading round: the garage/shop photo backdrop is opaque — don't render
    // the attract world under it (GPU the 3D stage needs, esp. on phones).
    this.game.setAttractRenderPaused(true);
    this.shopScreen.show(
      this.save,
      {
        onSave: (s) => {
          // M100: a NEW owned item = a purchase — queue it for the account
          // ledger (idempotent by item id; the server re-checks the balance).
          const before = this.save.cosmetics.owned;
          this.mergeGarageEdit(s);
          for (const id of this.save.cosmetics.owned) {
            if (!before.includes(id)) this.save = queuePurchase(this.save, id);
          }
          storeSave(this.save);
          this.syncPlayerLook();
          // M109: buying auto-equips, so a shop visit is a garage change too
          // (this also schedules the sync that posts the purchase).
          this.markGarageChanged();
        },
        onBack: () => this.showGarage(),
        // M110 (owner rule): SP can only be SPENT with a Racer ID — purchases
        // bank to the account. Exceptions: Big Kid (kids can't create
        // accounts and never see account CTAs — its economy stays on the
        // device, the M83 stance) and the account kill switch (pre-M100
        // behaviour exactly). Evaluated LIVE per render, and locked only on a
        // RESOLVED signed-out state — the brief boot-time 'loading' must not
        // flash the CTA at a player who is actually signed in.
        gameLock: () => this.paywallLock('shop') ?? null,
        // M139: signed out → the promo face (no grid, no actions but BUY).
        promo: () => {
          const l = this.promoView() ? this.paywallLock('shop') : undefined;
          if (!l) return null;
          return focus === 'customize'
            ? {
                focus,
                kicker: 'CUSTOMIZE',
                headline: 'MAKE IT YOURS',
                sub: this.catalogLine(),
                price: l.price,
                onBuy: l.onBuy,
                onLogIn: l.onLogIn,
              }
            : {
                focus,
                kicker: 'THE SHOP',
                headline: 'STYLE YOUR RIDE',
                sub: this.catalogLine(),
                price: l.price,
                onBuy: l.onBuy,
                onLogIn: l.onLogIn,
              };
        },
        buyLocked: () =>
          SUPABASE_ENABLED &&
          this.accountState.status === 'signedOut' &&
          this.save.settings.difficulty !== 'bigkid',
        onCreateAccount: () => this.showAccount(),
        // M130 — SP packs are sold on the WEB only for now: the native app
        // must not show an outside payment path (Apple 3.1.1) until the store
        // IAP lands in M2, Big Kid never sees purchases, and the wallet needs
        // accounts to exist at all. `startCheckout(pack)` navigates to
        // Stripe; the page comes back with &sp= and lands in the shop again.
        // SP_PACKS_ENABLED (owner, 2026-09-23) keeps the button off the web
        // until the Stripe prices exist — see config/features.ts.
        ...(SP_PACKS_ENABLED &&
        !isNativeApp() &&
        SUPABASE_ENABLED &&
        this.save.settings.difficulty !== 'bigkid'
          ? { onBuySp: (packId: string) => this.account.startCheckout(packId) }
          : {}),
        // M133 — INSIDE the store app the packs are sold through the store's
        // own purchase sheet (Apple 3.1.1 / Play Billing), never Stripe. The
        // hook only exists when a RevenueCat key is configured
        // (config/iap.ts), so a build without one sells nothing, as before.
        // The toast + balance repaint ride the same `spPackReturn` latch the
        // web return uses: armed before the sheet opens, fired by onState the
        // moment the bought points land (even on a later sync).
        ...(isNativeApp() &&
        nativeShims.iap &&
        SUPABASE_ENABLED &&
        this.save.settings.difficulty !== 'bigkid'
          ? {
              onBuySp: async (packId: string, onPaid?: () => void) => {
                // Armed BEFORE the store's sheet opens. A latch still waiting
                // on an earlier pack keeps its own `before`: that credit is
                // owed too (M134).
                this.spPackReturn ??= {
                  pack: packId,
                  before: this.save.purchasedPoints,
                  owed: 0,
                };
                const r = await this.account.buySpNative(packId, () => {
                  if (this.spPackReturn) {
                    this.spPackReturn.owed =
                      (this.spPackReturn.owed ?? 0) + (spPackById(packId)?.sp ?? 0);
                  }
                  onPaid?.();
                });
                // Nothing was charged and nothing else is on the way: disarm.
                // 'pending' stays armed, so a late credit is still announced.
                if (
                  (typeof r === 'string' || r.status === 'cancelled') &&
                  (this.spPackReturn?.owed ?? 0) <= 0
                ) {
                  this.spPackReturn = null;
                }
                return r;
              },
              spStorePrices: (ids: readonly string[]) => this.account.spStorePrices(ids),
            }
          : {}),
      },
      vehicleId,
    );
  }

  /** Confirm/pick your ride before a Quick Race (after the city is chosen). */
  private showGarageForQuick(cityIdx: number): void {
    this.hideAll();
    this.attractMenu(GameState.CitySelect);
    // Loading round: the garage/shop photo backdrop is opaque — don't render
    // the attract world under it (GPU the 3D stage needs, esp. on phones).
    this.game.setAttractRenderPaused(true);
    this.garage.show(this.save, {
      kicker: 'QUICK RACE',
      confirmLabel: 'START RACE',
      onConfirm: (id) => {
        this.applyVehicle(id);
        this.garage.hide();
        this.startQuickRace(cityIdx);
      },
      onBack: () => this.showCitySelect(),
      guest: this.isGuestView(),
    });
  }

  /** Persist + apply the player's chosen vehicle (shared by tour + quick race). */
  private applyVehicle(vehicleId: string): void {
    const changed = this.save.settings.playerVehicle !== vehicleId;
    this.save.settings.playerVehicle = vehicleId;
    storeSave(this.save);
    this.game.applySettings(this.save.settings);
    this.syncPlayerLook(); // the new ride wears ITS equipped shop look
    if (changed) this.markGarageChanged();
  }

  /**
   * M109 — this device just changed the GARAGE (a ride swap, an equip, a
   * purchase). Flag it so the next sync PUSHES to the account instead of
   * adopting from it, and kick that sync off now so the player's other devices
   * pick it up. Only call on a real change: a device that merely opened the
   * garage must never win over one that actually edited it.
   */
  private markGarageChanged(): void {
    this.save = markGarageDirty(this.save);
    storeSave(this.save);
    this.account.scheduleSync();
  }

  /**
   * M109 — fold a shop/garage edit onto the LIVE save.
   *
   * Both screens take a save SNAPSHOT when they open and hand back derived
   * copies of it, so writing one back wholesale reverts anything the account
   * sync wrote in the meantime — a flushed purchase outbox, an adopted garage,
   * a reconciled wallet. That was always latent; it is routine now, because
   * every equip schedules a sync while the screen is still open.
   *
   * The screens own exactly two things, so only those move: the equipped
   * LOADOUTS (their authority) and the SPEND, applied as a delta against the
   * snapshot rather than as an absolute. `owned` is unioned — purchases only
   * ever accumulate, from either side.
   */
  private mergeGarageEdit(s: SaveData): void {
    const spentDelta = s.spentPoints - (this.garageEditSpent ?? s.spentPoints);
    this.garageEditSpent = s.spentPoints;
    this.save = {
      ...this.save,
      spentPoints: Math.max(0, this.save.spentPoints + spentDelta),
      cosmetics: {
        owned: [...new Set([...this.save.cosmetics.owned, ...s.cosmetics.owned])],
        loadouts: s.cosmetics.loadouts,
      },
    };
  }

  /** Baseline for {@link mergeGarageEdit} — the spend as the open screen last
   *  reported it, so repeated edits in one visit each apply their own delta. */
  private garageEditSpent: number | null = null;

  /** The player's persisted ride, as carried on online join URLs (M51). */
  private vehicleParam(): string {
    return encodeURIComponent(this.save.settings.playerVehicle || 'sports');
  }

  // ----- Racing ------------------------------------------------------------

  /** M121/M123 — HARD GUARD, the Big Kid belt-and-braces shape (M83), now
   *  BROWSE-FIRST: for a non-owner QUICK RACE *is* the teaser — one tap from
   *  the title into a free NYC race, no account, no paywall in the way. The
   *  paywall only fronts once the soft cap is spent (quickRaceRoute, pure +
   *  tested), and even then its own "play the free race" escape still races.
   *  Tour / Online / Shop keep their unconditional guards below. `teaser` is
   *  the sanctioned bypass flag (set by startTeaserRace's re-entry). */
  private startQuickRace(cityIdx: number, teaser = false): void {
    if (!teaser) {
      const route = quickRaceRoute(this.purchaseGateArmed(), teaserRacesLeft(this.save));
      if (route === 'teaser') {
        this.startTeaserRace(); // re-enters with teaser=true, NYC forced
        return;
      }
      if (route === 'cap') {
        this.showPurchase('cap');
        return;
      }
    }
    // M123: remember whether THIS race is a teaser race — its finish ticks
    // the soft-cap counter in onRaceFinished's offline branch. A normal quick
    // race clears it (every launch point resets this flag).
    this.teaserRace = teaser;
    this.leaveOnline();
    this.mode = 'quick';
    this.hideAll();
    audioEngine.setDuck(1);
    this.setMood(CITIES[cityIdx].id);
    // M67: the city-splash intro fronts OFFLINE quick races too (parity with
    // online single races). Loading round: the splash is now ALSO the loading
    // screen — it shows instantly, the city builds in slices behind it, the
    // GPU warms up, and only then does the countdown start (tap = start now
    // once ready). See launchOfflineRace.
    void this.launchOfflineRace(CITIES[cityIdx], (onRelease) =>
      this.raceIntro.showCity(CITIES[cityIdx].id, {
        // M147: the title card a non-owner tapped reads DEMO RACE — the
        // splash it opens says the same.
        kicker: teaser ? 'DEMO RACE' : 'QUICK RACE',
        racers: this.fieldSize(),
        difficulty: this.save.settings.difficulty,
        onRelease,
        loading: true,
      }),
    );
  }

  /** Loading round: the phase label the splash shows per warm-up phase. */
  private static warmLabel(phase: WarmupProgress['phase']): string {
    switch (phase) {
      case 'assets':
        return 'LOADING LANDMARKS';
      case 'textures':
        return 'LOADING TEXTURES';
      case 'shaders':
        return 'COMPILING SHADERS';
      case 'render':
        return 'FINAL CHECKS';
      default:
        return 'READY';
    }
  }

  /**
   * Loading round — the OFFLINE race-start pipeline (quick races + direct
   * tour entries). Order matters:
   *   1. show the splash FIRST and let it paint (two frames) — the old flow
   *      built the city synchronously inside the tap handler, so the START
   *      button looked dead for 1-3 s on a phone and an impatient second tap
   *      landed on the splash (which then cancelled the start: the audit's
   *      "drone view, race never loads");
   *   2. build the city in slices (loadCityAsync) with progress;
   *   3. warm the GPU (assets → textures → shaders → warm renders);
   *   4. setReady → the countdown starts after the min time or on a tap.
   * A newer launch / hideAll supersedes this one (raceLaunchGen). Any
   * exception is reported (errorlog) and the player is never stranded: if a
   * world exists we race on it, otherwise we go back to the title.
   */
  private async launchOfflineRace(
    city: City,
    showIntro: (onRelease: () => void) => void,
  ): Promise<void> {
    const gen = ++this.raceLaunchGen;
    const difficulty = this.save.settings.difficulty;
    const field = this.fieldSize();
    const name = city.name.toUpperCase();
    const release = (): void => {
      this.game.beginRace();
      this.raceIntro.raceReady();
      this.armRaceStartWatchdog();
    };
    showIntro(release);
    this.raceIntro.setProgress(0.02, `BUILDING ${name}`);
    beginForeground(); // loading round 2: THIS is what the player waits on — no idle gating
    try {
      await nextFrame();
      await nextFrame(); // the splash has painted — now the heavy work
      if (gen !== this.raceLaunchGen) return;
      const built = this.game.isWorldLoaded(city, difficulty, field)
        ? true
        : await this.game.loadCityAsync(city, difficulty, field, (f) =>
            this.raceIntro.setProgress(0.02 + 0.33 * f, `BUILDING ${name}`),
          );
      if (!built || gen !== this.raceLaunchGen) return;
      if (!this.game.worldWarm) {
        await this.game.warmUpRace((p) =>
          this.raceIntro.setProgress(0.35 + 0.65 * p.frac, Flow.warmLabel(p.phase)),
        );
      }
      if (gen !== this.raceLaunchGen) return;
      this.raceIntro.setReady();
    } catch (e) {
      console.error('[Flow] offline race launch failed', e);
      reportError('race.launch', e);
      if (gen !== this.raceLaunchGen) return;
      if (this.game.worldReady) {
        this.raceIntro.setReady(); // race on whatever world we have
      } else {
        this.raceIntro.hide();
        this.showTitle();
      }
    } finally {
      endForeground();
    }
  }

  /** Loading round: belt-and-braces — if, 2 s after a release, the game is
   *  still in a menu state with no screen up, the start threw somewhere the
   *  intro could not catch. Report it and go home instead of leaving the
   *  player on the attract flythrough with no way out (the audit's #1). */
  private armRaceStartWatchdog(): void {
    window.setTimeout(() => {
      const st = this.game.state;
      if (st !== GameState.Menu && st !== GameState.CitySelect) return;
      // Any menu screen still up means the player has somewhere to be.
      const anyScreenUp = Array.from(this.parentEl.querySelectorAll<HTMLElement>('.screen')).some(
        (el) => getComputedStyle(el).display !== 'none',
      );
      if (anyScreenUp) return;
      reportError('race.start.watchdog', `state ${st} after release`);
      console.warn('[Flow] race never started after release — returning to the title');
      this.showTitle();
    }, 2000);
  }

  /** The offline field size — FIXED at you + 6 CPU rivals (M87 owner rule). */
  private fieldSize(): number {
    return OFFLINE_RIVALS + 1;
  }

  /** M87: this save's rolled tour route as City objects (canonical fallback). */
  private tourRoute(): City[] {
    return (this.save.tour?.order ?? CITIES.map((c) => c.id)).map((id) => cityById(id));
  }

  private restartRace(): void {
    this.results.hide();
    audioEngine.setDuck(1);
    this.game.beginRace();
  }

  private beginNewTour(): void {
    if (this.purchaseGateArmed()) {
      this.showPurchase('gate', 'tour');
      return;
    }
    this.tourFinalRace = null; // a fresh tour — the old finale stats are history
    this.save = startTour(this.save);
    storeSave(this.save);
    this.startNextTourRace();
  }

  private continueTour(): void {
    if (this.purchaseGateArmed()) {
      this.showPurchase('gate', 'tour');
      return;
    }
    if (!this.save.tour) {
      this.beginNewTour();
      return;
    }
    this.startNextTourRace();
  }

  private startNextTourRace(viaGlobe = false): void {
    const idx = tourCityIndex(this.save);
    if (idx === null) {
      this.showTourSummary();
      return;
    }
    this.leaveOnline();
    this.mode = 'tour';
    this.teaserRace = false; // M123: tour races never tick the teaser cap
    this.hideAll();
    audioEngine.setDuck(1);
    // M67: a race reached VIA the globe flight already had its transition — drop
    // straight in. A DIRECT tour entry (new tour, or Continue from the menu) gets
    // the World Tour Cup intro first, parity with online tours. Offline, the intro
    // starts the countdown after its minimum time, then fades onto it.
    // M89 music: the city mood is set as EARLY as possible — viaGlobe the song
    // is already fading in with the flight; a direct entry starts it over the
    // Cup intro — and the same song simply continues through the race.
    // (Mood is set BEFORE loadCity: WebAudio ramps run on the audio thread, so
    // the fade stays smooth while the main thread builds the city.)
    // M87: the tour's cities run in THIS save's rolled order, not CITIES order.
    const city = tourCityAt(this.save, idx);
    if (viaGlobe) {
      this.setMood(city.id);
      // Loading round: the globe flight pre-built + warmed this stop
      // (startGlobeTo) — only rebuild if that was skipped/cancelled.
      if (!this.game.isWorldLoaded(city, this.save.settings.difficulty, this.fieldSize())) {
        this.game.loadCity(city, this.save.settings.difficulty, this.fieldSize());
      }
      this.game.beginRace();
      this.armRaceStartWatchdog();
      // Not warm (a skipped flight): warm during the countdown — async, and
      // the live frames already draw, so no warm renders.
      if (!this.game.worldWarm) void this.game.warmUpRace(undefined, { render: false });
      return;
    }
    this.setMood(city.id); // the city's song fades in over the Cup intro (M89)
    // Tour races use the SAME persisted difficulty as Quick Race (owner rule
    // 2026-07-15 — this replaced the old per-city easy→legendary tour ramp).
    const raceNum = (this.save.tour?.results.length ?? 0) + 1;
    void this.launchOfflineRace(city, (onRelease) =>
      this.raceIntro.showTour(
        tourLength(),
        raceNum,
        this.fieldSize(),
        () => {
          this.setMood(city.id); // idempotent re-assert — the song just continues
          onRelease();
        },
        this.tourRoute(), // the intro globe flies THIS tour's route (M87)
        true,
      ),
    );
  }

  /** "Next City →" on tour results: fly the globe to the next stop (after the
   *  last city: the championship summary). */
  private advanceTour(): void {
    this.results.hide();
    if (isTourComplete(this.save)) {
      this.showTourSummary();
      return;
    }
    const idx = tourCityIndex(this.save);
    if (idx === null) {
      this.showTourSummary();
      return;
    }
    void this.startGlobeTo(idx);
  }

  /** The M9 flying-globe transition: roll the Earth from the city just raced
   *  to the next stop, then drop into its countdown. Lazy-loads the globe
   *  module (its own chunk); any load failure just skips straight to racing. */
  private async startGlobeTo(nextIdx: number): Promise<void> {
    const from = this.game.currentCity;
    const to = tourCityAt(this.save, nextIdx); // M87: the rolled order's next stop
    this.hideAll();
    audioEngine.setDuck(1);
    // M89: the next city's song fades in with the flight (the previous city's
    // song, still rolling from the results screen, hands off underneath).
    this.setMood(to.id);
    this.game.enterGlobe();
    this.globeScreen.show(from, to, nextIdx + 1, tourLength());
    this.globeActive = true;
    // Loading round: the flight is several seconds the main scene is NOT
    // drawn — build the next city behind it, in slices, and warm its GPU
    // state (no warm renders: the canvas shows the globe). endGlobe then
    // drops straight into a ready, warm world.
    void this.game
      .loadCityAsync(to, this.save.settings.difficulty, this.fieldSize())
      .then((ok) => (ok ? this.game.warmUpRace(undefined, { render: false }) : null))
      .catch((e) => reportError('tour.preload', e));
    try {
      const { createGlobeFlight } = await import('@/world/Globe');
      if (!this.globeActive) return; // skipped before the module arrived
      const flight = createGlobeFlight(from, to, CITIES);
      flight.onDone = () => this.endGlobe();
      this.flight = flight;
      this.game.setGlobe(flight);
    } catch {
      this.endGlobe(); // module failed to load — just go race
    }
  }

  private skipGlobe(): void {
    if (!this.globeActive) return;
    // Online tour: the SERVER controls the next race — skipping just fast-forwards
    // the local flight; it must NOT advance an offline tour.
    if (this.onlineTour) {
      this.flight?.finish();
      return;
    }
    if (this.flight)
      this.flight.finish(); // fires onDone → endGlobe
    else this.endGlobe(); // module still loading
  }

  private endGlobe(): void {
    if (!this.globeActive) return;
    this.globeActive = false;
    this.veil.flash(); // blink over the globe → race-world swap (M65)
    this.game.setGlobe(null);
    this.flight?.dispose();
    this.flight = null;
    this.globeScreen.hide();
    this.startNextTourRace(true); // viaGlobe: the flight WAS the transition — no intro
  }

  /** `quiet` (M94): the celebration cinematic already fired the champion
   *  fanfare — don't replay it under the standings. */
  private showTourSummary(quiet = false): void {
    const tour = this.save.tour;
    if (!tour) {
      this.showTitle();
      return;
    }
    this.hideAll();
    this.attractMenu(GameState.Menu);
    // M96: the final-standings page is the Champion song's moment too — full
    // level (no results duck), continuing seamlessly from the cinematic.
    this.setMood('champion');
    audioEngine.setDuck(1);
    const champion = playerIsChampion(tour.results);
    if (!quiet) {
      if (champion) sfxChampion();
      else sfxFanfare(2);
    }
    // M78: the championship bonus banked at the final race reveals here.
    // M94: the final race's stats ride along as the toggle view.
    this.results.showTourSummary(
      {
        table: championship(tour.results),
        trophies: tourTrophies(tour.results),
        champion,
        championshipBonus: this.pendingTourBonus?.guest
          ? null
          : (this.pendingTourBonus?.award ?? null),
        spTotal: this.save.lifetimePoints,
        spBank: spBalance(this.save),
        grantedVehicles: this.save.unlockedVehicles,
        newlyUnlocked: this.pendingTourBonus?.newlyUnlocked ?? [],
        reducedMotion: this.save.settings.reducedMotion,
        // M100: a guest's championship bonus went to the pot — the summary
        // shows the guest card + the CREATE RACER ID push instead.
        guestPending: this.pendingTourBonus?.guest
          ? this.guestPending(this.pendingTourBonus.award)
          : undefined,
      },
      this.tourFinalRace ?? undefined,
    );
    this.pendingTourBonus = null;
  }

  /** M94: the tour-finale podium celebration cinematic. Resolves with whether
   *  it actually played through (false = something else tore the flow down —
   *  the caller must NOT navigate) and whether the congrats fanfare already
   *  fired inside the clip. Reduced motion skips straight through. */
  private async playTourCelebration(
    table: ChampionshipRow[],
  ): Promise<{ played: boolean; congrats: boolean }> {
    if (this.save.settings.reducedMotion) return { played: true, congrats: false };
    const podium: PodiumRacerSpec[] = table.slice(0, 3).map((row, i) => {
      const app = this.game.racerAppearance(row.racerId);
      return {
        place: i + 1,
        name: row.name,
        color: row.color,
        vehicleId: app.vehicleId,
        look: app.look,
        isLocal: row.isPlayer,
      };
    });
    const champion = table[0]?.isPlayer ?? false;
    let congrats = false;
    this.game.setAttractRenderPaused(true); // the clip is opaque — skip the world render
    const res = await this.podiumClip.play(podium, {
      onCongrats: () => {
        congrats = true;
        if (champion) sfxChampion();
        else sfxFanfare(2);
      },
    });
    this.game.setAttractRenderPaused(false);
    return { played: res !== 'cancelled', congrats };
  }

  // ----- Race finished -----------------------------------------------------

  private onRaceFinished(result: GameEvents['raceFinished']): void {
    // M112: the onboarding demo race records NOTHING and awards NOTHING —
    // driving the whole course to the line just completes the lesson.
    if (this.demoRace) {
      this.demoRace.handleRaceFinished();
      return;
    }
    // Online TOUR: the server's official result drives the next screen (the tour
    // intermission or the championship summary) — don't show single-race results.
    // Until that lands, show a brief "waiting for the others" interstitial so the
    // first finisher isn't staring at a frozen race (instant when you're alone).
    if (this.onlineTour) {
      if (!this.tourResultShown) {
        // M74/M75: watch the racers still on course instead of a static waiting
        // card — cycle with ‹ ›, or SKIP to a waiting room. Falls back to the
        // card when nobody's left to watch (we finished last / were cut off).
        if (this.game.enterSpectate(this.rosterFilled, () => this.showTourWaiting())) return;
        this.showTourWaiting();
      }
      return;
    }

    // ONLINE quick race (M75): award lifetime points now (my place is locked the
    // moment I cross), but DEFER the results screen to the server's {t:'raceOver'}
    // — so early finishers spectate (or wait) until the race is actually decided,
    // and every client's table reflects the final field.
    if (this.online !== null) {
      if (this.onlineAwardArmed) {
        // M63 latch: once per {t:'begin'} — a mid-race rejoin's re-emitted finish
        // can't double-award. Unlocks + the M78 breakdown ride along to the
        // deferred results screen. M100: routed (account outbox / guest pot)
        // under the race's shared idempotency key.
        this.onlineAwardArmed = false;
        const bank = this.bankRaceAward(
          result.playerPlace,
          'race',
          this.onlineRaceEventId || `q:${crypto.randomUUID()}`,
        );
        this.pendingOnlineUnlocks = bank.newlyUnlocked;
        this.pendingOnlineAward = !bank.guest && bank.award.total > 0 ? bank.award : null;
        this.pendingGuestAward = bank.guest && bank.award.total > 0 ? bank.award : null;
      }
      if (this.game.enterSpectate(this.rosterFilled, () => this.showQuickWaiting())) return;
      this.showQuickWaiting(); // finished last (nobody to watch) → wait for raceOver
      return;
    }

    // OFFLINE: record the result + show it immediately (online never touches the
    // solo per-city records — best times / unlocks / tour progression).
    // M123: a finished TEASER race consumes one free race (the soft cap).
    // Deliberately HERE, in the offline branch only: the How-To-Play demo
    // returned at the top (this.demoRace) so it is uncapped by construction,
    // and online races took the branches above.
    const wasTeaser = this.teaserRace;
    if (wasTeaser) this.save = recordTeaserRace(this.save);
    const city = this.game.currentCity;
    let playerTimeMs: number | null = null;
    for (const e of result.entries) if (e.isPlayer) playerTimeMs = e.timeMs;
    const places: number[] = [];
    for (const e of result.entries) places[e.racerId] = e.place;
    const prevBest = cityRecord(this.save, city.id).bestTimeMs;
    const isNewBestTime = playerTimeMs !== null && (prevBest === null || playerTimeMs < prevBest);
    this.save = recordRaceResult(this.save, {
      cityId: city.id,
      places,
      playerTimeMs,
      mode: this.mode,
    });
    // M78: the race's Skill-Point payout (base + podium bonus) → the results
    // card. The career total shown there EXCLUDES any championship bonus —
    // that reveal belongs to the summary screen. M100: routed by the account
    // rule (linked → outbox too; guest → the pot + the CREATE RACER ID card).
    const bank = this.bankRaceAward(result.playerPlace, 'race', `o:${crypto.randomUUID()}`);
    const spAfterRace = this.save.lifetimePoints;
    // Tour finale: bank a top-3 championship bonus NOW (the summary can be
    // skipped by quitting — points must never depend on a screen being seen),
    // but stash it for the summary to reveal. Owner rule (2026-10-02): every
    // top-3 championship pays its full bonus, offline exactly like online.
    this.pendingTourBonus = null;
    if (this.mode === 'tour' && this.save.tour && isTourComplete(this.save)) {
      const table = championship(this.save.tour.results);
      const champPlace = table.findIndex((r) => r.isPlayer) + 1;
      const res = bankOfflineFinale(
        this.save,
        champPlace,
        `of:${crypto.randomUUID()}`,
        this.account.enabled,
      );
      this.save = res.save;
      if (res.route === 'account') this.account.scheduleSync();
      if (res.award.total > 0) {
        this.pendingTourBonus = {
          award: res.award,
          newlyUnlocked: res.newlyUnlocked,
          guest: res.route === 'guest',
        };
      }
    }
    storeSave(this.save);

    const tourRace = this.mode === 'tour' ? (this.save.tour?.results.length ?? null) : null;
    const ctx: ResultsContext = {
      mode: this.mode,
      cityName: city.name,
      entries: result.entries,
      playerPlace: result.playerPlace,
      playerTimeMs,
      isNewBestTime,
      newlyUnlocked: bank.newlyUnlocked,
      award: !bank.guest && bank.award.total > 0 ? bank.award : undefined,
      guestPending: bank.guest && bank.award.total > 0 ? this.guestPending(bank.award) : undefined,
      spTotal: spAfterRace,
      spBank: Math.max(0, spAfterRace + this.save.purchasedPoints - this.save.spentPoints),
      grantedVehicles: this.save.unlockedVehicles,
      reducedMotion: this.save.settings.reducedMotion,
      tourRace,
      tourTotal: tourLength(),
      isLastTourRace: this.mode === 'tour' && isTourComplete(this.save),
      online: false,
    };
    // M158c (owner, "two steps"): a tour race's results lead on to the TOUR
    // STANDINGS (route, overall ranking with movement, races left).
    if (this.mode === 'tour' && this.save.tour && !ctx.isLastTourRace) {
      const tour = this.save.tour;
      const len = tourLength();
      ctx.tourStep = {
        raceNum: tour.results.length,
        tourLen: len,
        stops: Array.from({ length: len }, (_, i) => ({
          name: tourCityAt(this.save, i).name,
          place: tour.results[i]?.places[0] ?? null,
        })),
        table: championship(tour.results),
        prevTable: championship(tour.results.slice(0, -1)),
      };
    }

    // M123: a TEASER race's results carry the storefront card (gold GET THE
    // FULL GAME CTA + races-left + the quiet sign-in link). The re-check on
    // purchaseGateArmed covers an account that resolved OWNERSHIP mid-race —
    // never sell to an owner. Never online / never the demo (both returned
    // above), and the sign-in link carries NO purchase intent — "already own
    // it?" is a restore, not a buy.
    // NEVER in Big Kid (M83, owner-locked): kids never see account or commerce
    // CTAs. The purchase GATE itself stays difficulty-blind (M111: the parent
    // signs in once) — this suppresses only the ADVERTISING surface.
    if (wasTeaser && this.purchaseGateArmed() && this.save.settings.difficulty !== 'bigkid') {
      ctx.storefront = {
        price: GAME_PRICE,
        freeRacesLeft: teaserRacesLeft(this.save),
        onBuy: () => {
          this.results.hide();
          this.showPurchase(teaserRacesLeft(this.save) <= 0 ? 'cap' : 'gate');
        },
        onSignIn: () => {
          this.results.hide();
          this.showAccount();
        },
      };
    }

    // M94: the FINAL tour race goes straight to the PODIUM CELEBRATION — the
    // top three vehicles push their trophy balls onto the winner's podium —
    // then the final standings. The race's own stats ride along behind the
    // summary's FINAL RACE STATS toggle instead of a screen of their own.
    if (ctx.isLastTourRace && this.save.tour) {
      sfxFanfare(result.playerPlace);
      audioEngine.setDuck(1); // the celebration scores at full music
      this.tourFinalRace = ctx;
      const table = championship(this.save.tour.results);
      this.hideAll();
      this.attractMenu(GameState.Menu);
      // M96: the CHAMPION song scores the whole finale (cinematic + standings).
      this.setMood('champion');
      void this.playTourCelebration(table).then(({ played, congrats }) => {
        if (played) this.showTourSummary(congrats);
      });
      return;
    }

    // M114: the FIRST offline WIN on the default (medium) difficulty offers a
    // one-time step up to HARD on this results panel. The latch is stored
    // BEFORE the screen shows (shown-once whatever they choose). The tour
    // FINALE is exempt by design (it returned above into the celebration +
    // summary, which never carry the module) — a first win there simply keeps
    // the offer for the next ordinary results screen.
    if (shouldOfferDifficultyStep(this.save, { online: false, place: result.playerPlace })) {
      this.save = { ...this.save, difficultyCtaShown: true };
      storeSave(this.save);
      ctx.difficultyCta = {
        onStepUp: () => {
          // Mirror the Settings screen's apply path: persist + live-apply.
          this.save.settings.difficulty = 'hard';
          storeSave(this.save);
          this.game.applySettings(this.save.settings);
        },
      };
    }
    audioEngine.setDuck(0.3);
    sfxFanfare(result.playerPlace);
    this.results.show(ctx);
  }

  /** M75: the online TOUR waiting room (skip spectate / finished last) — the
   *  server's official result replaces it. */
  private showTourWaiting(): void {
    if (!this.onlineTour) return;
    audioEngine.setDuck(0.3);
    this.tourIntermission.showWaiting(
      this.onlineTour.results.length + 1,
      this.onlineTour.len,
      this.game.currentCity.name,
    );
  }

  /** M75: the online QUICK-race waiting room — the server's {t:'raceOver'}
   *  replaces it with the results. */
  private showQuickWaiting(): void {
    audioEngine.setDuck(0.3);
    this.results.showWaiting(this.game.currentCity.name, () => this.showTitle());
  }

  /** M75: a QUICK online race is decided (server said so). End the on-track view
   *  (whether we were spectating, waiting, or the cut-off last car) and show the
   *  final results. Latched — finishers and the cut-off car converge here once. */
  private onNetRaceOver(o: GameEvents['netRaceOver']): void {
    this.onlineRacersForReport(); // M156: remember who raced, before teardown
    if (this.online === null || this.onlineTour || this.onlineResultShown) return;
    this.onlineResultShown = true;
    // A CUT-OFF player never crossed the line, so the finish-time award never
    // ran — award now from the official final place (finishers already did).
    if (this.onlineAwardArmed) {
      this.onlineAwardArmed = false;
      const myPlace = o.places[this.game.localSlot ?? 0] ?? 0;
      if (myPlace > 0) {
        // M100: SAME event id as the finish path — the server can only ever
        // bank this race once, whichever site reaches it first.
        const bank = this.bankRaceAward(
          myPlace,
          'race',
          this.onlineRaceEventId || `q:${crypto.randomUUID()}`,
        );
        this.pendingOnlineUnlocks = bank.newlyUnlocked;
        this.pendingOnlineAward = !bank.guest && bank.award.total > 0 ? bank.award : null;
        this.pendingGuestAward = bank.guest && bank.award.total > 0 ? bank.award : null;
      }
    }
    this.game.endRaceView();
    this.hideAll();
    // M143: in the SERVER's official order (as the tour finale does) — the
    // local standings are a prediction and can differ at the line.
    const result = this.game.currentResult(o.places, o.times);
    audioEngine.setDuck(0.3);
    sfxFanfare(result.playerPlace);
    this.results.show({
      mode: this.mode,
      cityName: this.game.currentCity.name,
      entries: result.entries,
      playerPlace: result.playerPlace,
      playerTimeMs: result.entries.find((e) => e.isPlayer)?.timeMs ?? null,
      isNewBestTime: false,
      newlyUnlocked: this.pendingOnlineUnlocks,
      award: this.pendingOnlineAward ?? undefined,
      guestPending: this.pendingGuestAward ? this.guestPending(this.pendingGuestAward) : undefined,
      spTotal: this.save.lifetimePoints,
      spBank: spBalance(this.save),
      grantedVehicles: this.save.unlockedVehicles,
      reducedMotion: this.save.settings.reducedMotion,
      tourRace: null,
      tourTotal: tourLength(),
      isLastTourRace: false,
      online: true,
    });
    this.pendingOnlineUnlocks = [];
    this.pendingOnlineAward = null;
    this.pendingGuestAward = null;
  }

  // ----- Pause -------------------------------------------------------------

  private togglePause(): void {
    // Escape/Start must not resume the race hidden BEHIND the settings panel.
    // M112: the demo race has no pause — Escape SKIPS it (the director's own
    // listener), so the pause machinery must stand down entirely.
    if (this.settingsOpen || this.demoRace) return;
    if (this.game.state === GameState.Paused) {
      this.pauseScreen.hide();
      this.game.resume();
    } else if (this.game.state === GameState.Racing) {
      this.game.pause();
      // Online the server races on: the overlay drops RESTART (meaningless) and
      // the client keeps ticking underneath (Game.update's neutral-input path).
      this.pauseScreen.setOnline(this.online !== null);
      this.pauseScreen.show();
    }
  }

  dispose(): void {
    this.demoRace?.dispose();
    this.podiumClip.dispose();
    this.leaderboardScreen.dispose();
    this.accountScreen.dispose();
    this.howToPlayScreen.dispose();
    this.shopScreen.dispose();
    this.spToast.dispose();
    this.welcomeToast.dispose();
    this.title.dispose();
    this.citySelect.dispose();
    this.results.dispose();
    this.pauseScreen.dispose();
    this.onlineScreen.dispose();
    this.onlineSetupScreen.dispose();
    this.lobbyScreen.dispose();
    this.raceLobby.dispose();
    this.raceIntro.dispose();
    this.veil.dispose();
    this.matchmaking?.cancel();
    this.tourIntermission.dispose();
  }
}
